import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, X, Search, ShoppingCart, Package, Building2, Truck, FileText, CheckCircle, XCircle, Printer, Eye, ShieldCheck, PackageCheck, Wallet, CreditCard, Banknote, RefreshCw, Clock, RotateCcw } from 'lucide-react';
import {
  getPurchaseOrders, getPurchaseOrderById, createPurchaseOrder, updatePurchaseOrder, deletePurchaseOrder, updatePurchaseOrderStatus, verifyPurchaseOrderItems,
  getInventoriSpareparts, getGudang, getSupplier,
  getPenerimaanPembelian, getPenerimaanPembelianById, createPenerimaanPembelian, updatePenerimaanPembelian, deletePenerimaanPembelian, setMetodePembayaranPenerimaan,
  getFakturPembelian, getFakturPembelianById, createFakturPembelian, updateFakturPembelian, deleteFakturPembelian,
} from '../../api';
import { useAuth } from '../../AuthContext';
import SearchableSelect from '../../components/SearchableSelect';
import logoPerusahaan from '../../utils/logoPerusahaan';

// Helper: rentang tanggal awal & akhir bulan berjalan (YYYY-MM-DD, dihitung lokal agar tidak kena bug timezone UTC)
const getCurrentMonthRange = () => {
  const now = new Date();
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return {
    awal: fmt(new Date(now.getFullYear(), now.getMonth(), 1)),
    akhir: fmt(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

const TABS = [
  { key: 'po', label: 'PO', icon: ShoppingCart, color: '#5e72e4', desc: 'Pesanan ke supplier' },
  { key: 'verifikasi', label: 'Verifikasi PO', icon: ShieldCheck, color: '#f5365c', desc: 'Menunggu terima barang' },
  { key: 'pending', label: 'PO Pending', icon: Clock, color: '#f59e0b', desc: 'Menunggu keputusan / diajukan ulang' },
  { key: 'ditolak', label: 'PO Ditolak', icon: XCircle, color: '#dc2626', desc: 'PO ditolak total / bisa diajukan ulang' },
  { key: 'penerimaan', label: 'Penerimaan Pembelian', icon: PackageCheck, color: '#2dce89', desc: 'Barang diterima' },
  { key: 'faktur', label: 'Faktur Pembelian', icon: FileText, color: '#fb6340', desc: 'Invoice dari supplier' },
];

const METODE_LIST = [
  { key: 'Hutang', label: 'Hutang', desc: 'Utang Usaha (kredit)' },
  { key: 'Transfer', label: 'Transfer', desc: 'Bank (transfer)' },
  { key: 'Cash', label: 'Cash', desc: 'Kas (tunai)' },
  { key: 'Petty Cash', label: 'Petty Cash', desc: 'Kas Kecil (1120)' },
];

export default function PurchaseOrder() {
  const { user, hasMenuAction } = useAuth();
  const [activeTab, setActiveTab] = useState('po');

  const [data, setData] = useState([]);          // PO list
  const [penerimaan, setPenerimaan] = useState([]);
  const [faktur, setFaktur] = useState([]);
  const [spareparts, setSpareparts] = useState([]);
  const [gudangList, setGudangList] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Filter tanggal — default bulan berjalan (kosongkan untuk menampilkan semua periode)
  const [tglMulai, setTglMulai] = useState(getCurrentMonthRange().awal);
  const [tglAkhir, setTglAkhir] = useState(getCurrentMonthRange().akhir);

  // Modal PO (buat/edit)
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Detail modal (lihat)
  const [detail, setDetail] = useState(null); // { kind, header, items }

  // Terima barang
  const [terima, setTerima] = useState(null); // { po, form }

  // Edit penerimaan
  const [editTerima, setEditTerima] = useState(null); // { header, form }

  // Metode pembayaran
  const [bayar, setBayar] = useState(null); // penerimaan

  // Faktur create/edit
  const [fakturModal, setFakturModal] = useState(null); // { editing, form }

  const [form, setForm] = useState({
    tanggal: new Date().toISOString().slice(0, 10), supplier_name: '', supplier_phone: '',
    supplier_address: '', gudang_id: '', notes: '', diperiksa_oleh: '', dibuat_oleh: '',
    ppn_aktif: false, ppn_persen: 11,
    items: [{ sparepart_id: '', unit: '', qty: 1, harga_satuan: 0, keterangan: '' }]
  });

  const load = () => {
    setLoading(true);
    Promise.all([getPurchaseOrders(), getPenerimaanPembelian(), getFakturPembelian(), getInventoriSpareparts({}), getGudang(), getSupplier()])
      .then(([res, prRes, fRes, spRes, gRes, supRes]) => {
        setData(res.data || []);
        setPenerimaan(prRes.data || []);
        setFaktur(fRes.data || []);
        setSpareparts(spRes.data || []);
        setGudangList(gRes.data || []);
        setSuppliers(supRes.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh gudang & supplier list when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        getGudang().then(res => setGudangList(res.data || [])).catch(() => {});
        getSupplier().then(res => setSuppliers(res.data || [])).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rupiah = (v) => `Rp ${(Number(v) || 0).toLocaleString('id-ID')}`;

  const sparepartMap = {};
  spareparts.forEach(s => { sparepartMap[s.id] = s; });

  // ============ FILTERS ============
  const q = search.toLowerCase();
  // Filter tanggal (YYYY-MM-DD) — kosong = semua periode
  const inDateRange = (t) => !tglMulai || !tglAkhir || ((t || '') >= tglMulai && (t || '') <= tglAkhir);
  // Tab PO: murni hanya Draft (siap diverifikasi/diedit).
  // PO Diverifikasi pindah penuh ke tab Verifikasi PO; Pending ke tab PO Pending; Ditolak ke tab PO Ditolak.
  const filteredPO = data.filter(d =>
    d.status === 'Draft' && inDateRange(d.tanggal) && (!q || d.no_po?.toLowerCase().includes(q) || d.supplier_name?.toLowerCase().includes(q))
  );
  const filteredDitolak = data.filter(d =>
    d.status === 'Ditolak' && inDateRange(d.tanggal) && (!q || d.no_po?.toLowerCase().includes(q) || d.supplier_name?.toLowerCase().includes(q))
  );
  const filteredVerif = data.filter(d =>
    d.status === 'Diverifikasi' && inDateRange(d.tanggal) && (!q || d.no_po?.toLowerCase().includes(q) || d.supplier_name?.toLowerCase().includes(q))
  );
  // Tab PO Pending: PO berstatus Pending (ditunda total) ATAU PO yang masih punya item berstatus Pending
  const filteredPending = data.filter(d =>
    (d.status === 'Pending' || (d.pending_items || 0) > 0) && inDateRange(d.tanggal) && (!q || d.no_po?.toLowerCase().includes(q) || d.supplier_name?.toLowerCase().includes(q))
  );
  const filteredPenerimaan = penerimaan.filter(d =>
    inDateRange(d.tanggal) && (!q || d.no_penerimaan?.toLowerCase().includes(q) || d.no_po?.toLowerCase().includes(q) || d.supplier_name?.toLowerCase().includes(q))
  );
  const filteredFaktur = faktur.filter(d =>
    inDateRange(d.tanggal) && (!q || d.no_faktur?.toLowerCase().includes(q) || d.no_penerimaan?.toLowerCase().includes(q) || d.supplier_name?.toLowerCase().includes(q))
  );

  // ============ TAB SUMMARY (ikuti filter tanggal yang aktif) ============
  const verifNilai = filteredVerif.reduce((s, d) => s + (d.total_amount || 0), 0);
  const terimaNilai = filteredPenerimaan.reduce((s, d) => s + (d.total_amount || 0), 0);
  const fakturBelum = filteredFaktur.filter(f => f.status === 'Belum Dibayar').reduce((s, f) => s + (f.total_amount || 0), 0);

  // ============ PO CRUD ============
  const handleSupplierSelect = (supId) => {
    if (!supId) {
      setForm(f => ({ ...f, supplier_name: '', supplier_phone: '', supplier_address: '' }));
      return;
    }
    const sup = suppliers.find(s => s.id === parseInt(supId));
    if (sup) {
      setForm(f => ({ ...f, supplier_name: sup.nama, supplier_phone: sup.telepon || '', supplier_address: sup.alamat || '' }));
    }
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { sparepart_id: '', unit: '', qty: 1, harga_satuan: 0, keterangan: '' }] }));
  const updItem = (idx, field, value) => {
    const items = [...form.items]; items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, items });
  };
  const delItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    setError('');
    if (form.items.length === 0 || !form.items[0].sparepart_id) { setError('Minimal 1 item sparepart'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form, gudang_id: parseInt(form.gudang_id) || 0,
        ppn_aktif: !!form.ppn_aktif, ppn_persen: Number(form.ppn_persen) || 11,
        items: form.items.map(i => ({ sparepart_id: Number(i.sparepart_id), qty: Number(i.qty), harga_satuan: Number(i.harga_satuan), keterangan: i.keterangan }))
      };
      if (editing) {
        await updatePurchaseOrder(editing.id, payload);
      } else {
        await createPurchaseOrder(payload);
      }
      setShowModal(false); setEditing(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  const handleDeletePO = async (id) => {
    if (!window.confirm('Yakin hapus PO ini?')) return;
    try { await deletePurchaseOrder(id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal hapus'); }
  };

  const handleVerifikasi = (item) => {
    // Buka modal verifikasi per item: bisa tolak beberapa item / ubah qty disetujui
    openDetailPO(item, { verify: true });
  };

  const handleStatus = async (id, status) => {
    const labels = { 'Dikirim': 'mengirim PO ke supplier', 'Selesai': 'menyelesaikan PO', 'Dibatalkan': 'membatalkan PO' };
    if (!window.confirm(`Yakin ${labels[status] || 'mengubah status'} ini?`)) return;
    try {
      await updatePurchaseOrderStatus(id, status);
      load();
    } catch (err) { alert(err.response?.data?.error || 'Gagal update status'); }
  };

  const openModal = () => {
    setError(''); setEditing(null);
    setForm({
      tanggal: new Date().toISOString().slice(0, 10), supplier_name: '', supplier_phone: '',
      supplier_address: '', gudang_id: '', notes: '', diperiksa_oleh: '', dibuat_oleh: '',
      ppn_aktif: false, ppn_persen: 11,
      items: [{ sparepart_id: '', unit: '', qty: 1, harga_satuan: 0, keterangan: '' }]
    });
    setShowModal(true);
  };

  const openEdit = async (item) => {
    setError(''); setSaving(false);
    try {
      const res = await getPurchaseOrderById(item.id);
      const d = res.data;
      if (!d) { alert('Gagal memuat data PO'); return; }
      setEditing(item);
      setForm({
        tanggal: d.tanggal || new Date().toISOString().slice(0, 10),
        supplier_name: d.supplier_name || '', supplier_phone: d.supplier_phone || '',
        supplier_address: d.supplier_address || '', gudang_id: d.gudang_id || '',
        notes: d.notes || '',
        diperiksa_oleh: d.diperiksa_oleh || '',
        dibuat_oleh: d.dibuat_oleh || '',
        ppn_aktif: !!d.ppn_aktif, ppn_persen: Number(d.ppn_persen) || 11,
        items: (d.items || []).map(it => ({
          sparepart_id: it.sparepart_id, unit: it.unit || '', qty: it.qty,
          harga_satuan: it.harga_satuan, keterangan: it.keterangan || ''
        }))
      });
      setShowModal(true);
    } catch (err) { console.error('Gagal muat data edit', err); alert('Gagal memuat data PO'); }
  };

  // ============ DETAIL (Lihat) ============
  const openDetailPO = async (item, opts = {}) => {
    try {
      const res = await getPurchaseOrderById(item.id);
      const d = res.data;
      if (!d) return;
      // Mode verifikasi penuh: hanya untuk PO berstatus Draft, memungkinkan tolak item / ubah qty disetujui
      const verify = d.status === 'Draft' && (opts.verify !== false);
      // Mode proses item pending: hanya menampilkan item yang masih Pending pada PO yang sudah Diverifikasi
      const verifyPending = !!opts.verifyPending;
      let items = (d.items || []);
      if (verifyPending) {
        items = items.filter(it => it.status_verifikasi === 'Pending');
        if (items.length === 0) { alert('Tidak ada item pending untuk diproses'); return; }
      }
      items = items.map(it => ({
        ...it,
        status_verifikasi: it.status_verifikasi || 'Disetujui',
        qty_verifikasi: it.qty_verifikasi > 0 ? it.qty_verifikasi : (it.qty || 0),
      }));
      setDetail({ kind: 'po', title: verifyPending ? `Proses Item Pending — ${d.no_po}` : `Detail PO ${d.no_po}`, header: d, items, verify: verify || verifyPending, verifyPending });
    } catch (err) { alert('Gagal memuat detail PO'); }
  };

  const updVerifItem = (idx, field, value) => {
    if (!detail) return;
    const items = [...detail.items];
    items[idx] = { ...items[idx], [field]: value };
    setDetail({ ...detail, items });
  };

  const handleVerifikasiItem = async (keputusan) => {
    if (!detail || detail.kind !== 'po') return;
    if (keputusan === 'Ditolak' && !window.confirm('Tolak seluruh PO ini? Semua item akan ditandai Ditolak.')) return;
    setSaving(true);
    try {
      await verifyPurchaseOrderItems(detail.header.id, {
        keputusan,
        items: detail.items.map(it => ({
          item_id: it.id,
          status_verifikasi: it.status_verifikasi || 'Disetujui',
          qty_verifikasi: Number(it.qty_verifikasi) || 0,
        })),
      });
      setDetail(null);
      load();
      if (detail.verifyPending) {
        // Proses item pending selesai — tetap di tab Pending; PO otomatis hilang dari daftar bila semua item sudah diputuskan
        setActiveTab('pending');
      } else if (keputusan === 'Pending') {
        setActiveTab('pending');
      } else if (keputusan === 'Ditolak') {
        setActiveTab('po');
      } else {
        setActiveTab('verifikasi');
      }
    } catch (err) { alert(err.response?.data?.error || 'Gagal verifikasi item'); }
    finally { setSaving(false); }
  };

  const handleAjukanKembali = async (id) => {
    if (!window.confirm('Ajukan PO ini kembali ke tab PO? Status akan kembali Draft dan verifikasi item direset.')) return;
    try {
      await updatePurchaseOrderStatus(id, 'Draft');
      load();
      setActiveTab('po');
    } catch (err) { alert(err.response?.data?.error || 'Gagal mengajukan kembali'); }
  };

  const openDetailPenerimaan = async (item) => {
    try {
      const res = await getPenerimaanPembelianById(item.id);
      const d = res.data;
      if (!d) return;
      setDetail({ kind: 'penerimaan', title: `Detail Penerimaan ${d.no_penerimaan}`, header: d, items: d.items || [] });
    } catch (err) { alert('Gagal memuat detail penerimaan'); }
  };

  const openDetailFaktur = async (item) => {
    try {
      const res = await getFakturPembelianById(item.id);
      const d = res.data;
      if (!d) return;
      setDetail({ kind: 'faktur', title: `Detail Faktur ${d.no_faktur}`, header: d, items: d.items || [] });
    } catch (err) { alert('Gagal memuat detail faktur'); }
  };

  // ============ TERIMA BARANG ============
  const openTerima = async (item) => {
    try {
      const res = await getPurchaseOrderById(item.id);
      const d = res.data;
      if (!d) return;
      // Hanya item berstatus Disetujui (atau belum diverifikasi) yang bisa diterima barangnya.
      // Item Ditolak / Pending tidak masuk daftar terima barang.
      const items = (d.items || [])
        .filter(it => !it.status_verifikasi || it.status_verifikasi === 'Disetujui')
        .map(it => {
          const qtyPesan = Number(it.qty_verifikasi || it.qty || 0);
          const sisa = Math.max(qtyPesan - Number(it.qty_diterima || 0), 0);
          return {
            po_item_id: it.id, sparepart_id: it.sparepart_id, code: it.code, name: it.name,
            qty_dipesan: qtyPesan, qty_diterima: sisa, harga_satuan: Number(it.harga_satuan),
            keterangan: it.keterangan || '', sisa, diskon_persen: 0,
          };
        });
      setTerima({
        po: d,
        form: {
          po_id: d.id, tanggal: new Date().toISOString().slice(0, 10),
          gudang_id: d.gudang_id || '', notes: '', items,
          diskon_persen: 0, diskon_rupiah: 0, biaya_lain: 0, biaya_lain_catatan: '',
          ppn_aktif: !!d.ppn_aktif, ppn_persen: Number(d.ppn_persen) || 11,
          metode_pembayaran: '',
        },
      });
    } catch (err) { alert('Gagal memuat data PO untuk terima barang'); }
  };

  const updTerimaItem = (idx, value) => {
    if (!terima) return;
    const items = [...terima.form.items];
    const max = items[idx].sisa;
    let v = Number(value) || 0;
    if (v > max) v = max;
    items[idx] = { ...items[idx], qty_diterima: v };
    setTerima({ ...terima, form: { ...terima.form, items } });
  };

  const updTerimaItemDiskon = (idx, value) => {
    if (!terima) return;
    const items = [...terima.form.items];
    let v = Number(value) || 0;
    if (v < 0) v = 0;
    if (v > 100) v = 100;
    items[idx] = { ...items[idx], diskon_persen: v };
    setTerima({ ...terima, form: { ...terima.form, items } });
  };

  const updTerimaItemHarga = (idx, value) => {
    if (!terima) return;
    const items = [...terima.form.items];
    let v = Number(value) || 0;
    if (v < 0) v = 0;
    items[idx] = { ...items[idx], harga_satuan: v };
    setTerima({ ...terima, form: { ...terima.form, items } });
  };

  const updTerimaForm = (field, value) => {
    if (!terima) return;
    setTerima({ ...terima, form: { ...terima.form, [field]: value } });
  };

  const handleTerima = async () => {
    if (!terima) return;
    setSaving(true); setError('');
    const tForm = terima.form;
    if (tForm.items.length === 0 || tForm.items.every(i => !i.qty_diterima)) {
      setError('Minimal 1 item dengan qty diterima > 0');
      setSaving(false);
      return;
    }
    try {
      const payload = {
        po_id: tForm.po_id, tanggal: tForm.tanggal, gudang_id: parseInt(tForm.gudang_id) || 0, notes: tForm.notes,
        diskon_persen: Number(tForm.diskon_persen) || 0, diskon_rupiah: Number(tForm.diskon_rupiah) || 0,
        ppn_aktif: !!tForm.ppn_aktif, ppn_persen: Number(tForm.ppn_persen) || 11,
        biaya_lain: Number(tForm.biaya_lain) || 0, biaya_lain_catatan: tForm.biaya_lain_catatan || '',
        metode_pembayaran: tForm.metode_pembayaran || '',
        items: tForm.items.map(i => ({
          po_item_id: i.po_item_id, sparepart_id: Number(i.sparepart_id),
          qty_dipesan: Number(i.qty_dipesan), qty_diterima: Number(i.qty_diterima),
          harga_satuan: Number(i.harga_satuan), keterangan: i.keterangan || '',
          diskon_persen: Number(i.diskon_persen) || 0,
        })),
      };
      const res = await createPenerimaanPembelian(payload);
      setTerima(null);
      load();
      setActiveTab('penerimaan');
      alert(`Barang diterima: ${res.data.no_penerimaan}`);
    } catch (err) { setError(err.response?.data?.error || 'Gagal menyimpan penerimaan'); }
    finally { setSaving(false); }
  };

  // ============ EDIT PENERIMAAN ============
  const openEditPenerimaan = async (item) => {
    try {
      const res = await getPenerimaanPembelianById(item.id);
      const d = res.data;
      if (!d) return;
      const items = (d.items || []).map(it => ({
        po_item_id: it.po_item_id || 0, sparepart_id: it.sparepart_id, code: it.code, name: it.name,
        qty_dipesan: Number(it.qty_dipesan), qty_diterima: Number(it.qty_diterima),
        harga_satuan: Number(it.harga_satuan), keterangan: it.keterangan || '',
        sisa: Number(it.qty_dipesan), diskon_persen: Number(it.diskon_persen) || 0,
      }));
      setEditTerima({
        header: d,
        form: {
          tanggal: d.tanggal || new Date().toISOString().slice(0, 10),
          gudang_id: d.gudang_id || '', notes: d.notes || '', items,
          diskon_persen: Number(d.diskon_persen) || 0, diskon_rupiah: Number(d.diskon_rupiah) || 0,
          ppn_aktif: !!d.ppn_aktif, ppn_persen: Number(d.ppn_persen) || 11,
          biaya_lain: Number(d.biaya_lain) || 0, biaya_lain_catatan: d.biaya_lain_catatan || '',
          metode_pembayaran: d.metode_pembayaran || '',
        },
      });
    } catch (err) { alert('Gagal memuat data penerimaan'); }
  };

  const updEditTerimaItem = (idx, value) => {
    if (!editTerima) return;
    const items = [...editTerima.form.items];
    const max = items[idx].sisa;
    let v = Number(value) || 0;
    if (v > max) v = max;
    items[idx] = { ...items[idx], qty_diterima: v };
    setEditTerima({ ...editTerima, form: { ...editTerima.form, items } });
  };

  const updEditTerimaItemDiskon = (idx, value) => {
    if (!editTerima) return;
    const items = [...editTerima.form.items];
    let v = Number(value) || 0;
    if (v < 0) v = 0;
    if (v > 100) v = 100;
    items[idx] = { ...items[idx], diskon_persen: v };
    setEditTerima({ ...editTerima, form: { ...editTerima.form, items } });
  };

  const updEditTerimaItemHarga = (idx, value) => {
    if (!editTerima) return;
    const items = [...editTerima.form.items];
    let v = Number(value) || 0;
    if (v < 0) v = 0;
    items[idx] = { ...items[idx], harga_satuan: v };
    setEditTerima({ ...editTerima, form: { ...editTerima.form, items } });
  };

  const updEditTerimaForm = (field, value) => {
    if (!editTerima) return;
    setEditTerima({ ...editTerima, form: { ...editTerima.form, [field]: value } });
  };

  const handleEditTerima = async () => {
    if (!editTerima) return;
    setSaving(true); setError('');
    const f = editTerima.form;
    if (f.items.length === 0 || f.items.every(i => !i.qty_diterima)) {
      setError('Minimal 1 item dengan qty diterima > 0');
      setSaving(false);
      return;
    }
    try {
      await updatePenerimaanPembelian(editTerima.header.id, {
        tanggal: f.tanggal, gudang_id: parseInt(f.gudang_id) || 0, notes: f.notes,
        diskon_persen: Number(f.diskon_persen) || 0, diskon_rupiah: Number(f.diskon_rupiah) || 0,
        ppn_aktif: !!f.ppn_aktif, ppn_persen: Number(f.ppn_persen) || 11,
        biaya_lain: Number(f.biaya_lain) || 0, biaya_lain_catatan: f.biaya_lain_catatan || '',
        metode_pembayaran: f.metode_pembayaran || '',
        items: f.items.map(i => ({
          po_item_id: i.po_item_id, sparepart_id: Number(i.sparepart_id),
          qty_dipesan: Number(i.qty_dipesan), qty_diterima: Number(i.qty_diterima),
          harga_satuan: Number(i.harga_satuan), keterangan: i.keterangan || '',
          diskon_persen: Number(i.diskon_persen) || 0,
        })),
      });
      setEditTerima(null);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Gagal update penerimaan'); }
    finally { setSaving(false); }
  };

  const handleDeletePenerimaan = async (item) => {
    if (!window.confirm(`Yakin hapus penerimaan ${item.no_penerimaan}? Stok akan dikembalikan dan jurnal akuntansi terkait dihapus.`)) return;
    try { await deletePenerimaanPembelian(item.id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal hapus penerimaan'); }
  };

  // ============ METODE PEMBAYARAN ============
  const openBayar = (item) => {
    setError('');
    setBayar({ ...item, metode: item.metode_pembayaran || '' });
  };

  const handleBayar = async () => {
    if (!bayar) return;
    if (!bayar.metode) { setError('Pilih metode pembayaran terlebih dahulu'); return; }
    setSaving(true);
    try {
      await setMetodePembayaranPenerimaan(bayar.id, bayar.metode);
      setBayar(null);
      load();
      alert('Metode pembayaran disimpan & jurnal akuntansi berhasil dibuat');
    } catch (err) { setError(err.response?.data?.error || 'Gagal menyimpan metode pembayaran'); }
    finally { setSaving(false); }
  };

  // ============ FAKTUR ============
  const openFakturCreate = () => {
    setError(''); setSaving(false);
    setFakturModal({
      editing: null,
      form: { penerimaan_id: '', no_faktur: '', tanggal: new Date().toISOString().slice(0, 10), notes: '', status: 'Belum Dibayar' },
    });
  };

  const openFakturEdit = async (item) => {
    try {
      const res = await getFakturPembelianById(item.id);
      const d = res.data;
      if (!d) return;
      setError(''); setSaving(false);
      setFakturModal({
        editing: d,
        form: {
          penerimaan_id: d.penerimaan_id || '', no_faktur: d.no_faktur || '',
          tanggal: d.tanggal || new Date().toISOString().slice(0, 10), notes: d.notes || '', status: d.status || 'Belum Dibayar',
        },
      });
    } catch (err) { alert('Gagal memuat data faktur'); }
  };

  const handleFakturSave = async () => {
    if (!fakturModal) return;
    setError(''); setSaving(true);
    const f = fakturModal.form;
    try {
      if (fakturModal.editing) {
        await updateFakturPembelian(fakturModal.editing.id, {
          no_faktur: f.no_faktur, tanggal: f.tanggal, notes: f.notes, status: f.status,
        });
      } else {
        if (!f.penerimaan_id) { setError('Pilih penerimaan'); setSaving(false); return; }
        await createFakturPembelian({
          penerimaan_id: Number(f.penerimaan_id), no_faktur: f.no_faktur, tanggal: f.tanggal, notes: f.notes, status: f.status,
        });
      }
      setFakturModal(null);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Gagal menyimpan faktur'); }
    finally { setSaving(false); }
  };

  const handleDeleteFaktur = async (item) => {
    if (!window.confirm(`Yakin hapus faktur ${item.no_faktur}?`)) return;
    try { await deleteFakturPembelian(item.id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal hapus faktur'); }
  };

  // ============ PDF ============
  const buildDoc = () => {
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentW = pageW - 2 * margin;
    const primary = [79, 110, 247];
    const primaryDark = [59, 90, 227];
    const dark = [23, 43, 77];
    const gray = [136, 152, 170];
    const lightBg = [248, 249, 252];
    const fmtRupiah = (v) => `Rp ${(Number(v) || 0).toLocaleString('id-ID')}`;
    return { doc, pageW, pageH, margin, contentW, primary, primaryDark, dark, gray, lightBg, fmtRupiah, autoTable };
  };

  const drawHeader = (ctx, judul, noRef, tanggal, status) => {
    const { doc, pageW, margin, contentW, gray, dark } = ctx;
    const headerBg = [13, 29, 54];
    const teal = [57, 143, 153];

    // ===== 1. HEADER (mirip cetak Tagihan) =====
    // Logo perusahaan (kiri atas)
    try {
      doc.addImage(logoPerusahaan, 'PNG', margin, 3, 40, 22);
    } catch (e) { /* ignore logo error */ }

    // Judul dokumen (kanan atas)
    doc.setTextColor(...headerBg);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(judul, pageW - margin, 18, { align: 'right' });

    // Accent line
    doc.setDrawColor(...teal);
    doc.setLineWidth(1);
    doc.line(margin, 20, pageW - margin, 20);
    doc.setLineWidth(0.3);
    doc.setDrawColor(180, 180, 180);

    let y = 26;

    // ===== 2. PROFIL PT (kiri) + DATA DOKUMEN (kanan) =====
    const col2X = margin + (contentW / 2) + 6;

    // Kiri: Alamat Perusahaan
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    const addrLines = [
      'PT. SYNEXA GLOBAL TEKNO',
      'Graha Raya: Ruko Silktown Avenue Blok RK/5A-05',
      'Jl. Boulevard Silk Town, Kel. Pondok Jagung Timur',
      'Kec. Serpong Utara, Kota Tangerang Selatan 15320',
      'Jakarta: Office 8 SCBD Lt.17, Jl. Senopati',
      'www.synexa.co.id | info@synexa.co.id',
    ];
    addrLines.forEach((line, i) => {
      doc.text(line, margin, y + i * 3.5);
    });

    // Kanan: Data dokumen
    const invFields = [
      ['NO. DOKUMEN', noRef || '-'],
      ['TANGGAL', tanggal || '-'],
      ['STATUS', status || '-'],
    ];
    const labelW = 18;
    const rowH = 4.5;
    doc.setFontSize(7);
    invFields.forEach((f, i) => {
      const ry = y + i * rowH;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...gray);
      doc.text(f[0] + ':', col2X, ry);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...dark);
      doc.text(f[1], col2X + labelW + 2, ry);
    });

    y += Math.max(addrLines.length * 3.5 + 2, invFields.length * rowH + 2) - 3;
    return y;
  };

  const drawSupplierBlock = (ctx, d, yStart, label) => {
    const { doc, margin, contentW, gray, dark } = ctx;
    const sectionTitle = (title, yPos) => {
      doc.setFillColor(...ctx.primary);
      doc.rect(margin, yPos, 3, 8, 'F');
      doc.setTextColor(...dark);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(title, margin + 7, yPos + 6);
      return yPos + 14;
    };
    const drawField = (label2, value, x, yPos) => {
      doc.setTextColor(...gray);
      doc.setFontSize(7);
      doc.text(label2, x, yPos);
      doc.setTextColor(...dark);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(value || '-', x, yPos + 4);
      doc.setFont('helvetica', 'normal');
      return yPos + 11;
    };
    let y = sectionTitle(label || 'DATA SUPPLIER', yStart);
    const leftX = margin;
    const rightX = margin + contentW / 2 + 5;
    let ly = y;
    ly = drawField('Nama Supplier', d.supplier_name || '-', leftX, ly);
    ly = drawField('Alamat', d.supplier_address || '-', leftX, ly);
    if (d.supplier_phone) ly = drawField('Telepon', d.supplier_phone, leftX, ly);
    let ry = y;
    ry = drawField('Gudang', d.gudang_nama || '-', rightX, ry);
    return Math.max(ly, ry) + 4;
  };

  const generatePenerimaanPDF = async (item) => {
    if (!item) return;
    try {
      const res = await getPenerimaanPembelianById(item.id);
      const d = res.data;
      if (!d) return;
      const ctx = buildDoc();
      const { doc, pageW, pageH, margin, contentW, primary, dark, gray, lightBg, fmtRupiah, autoTable } = ctx;
      drawHeader(ctx, 'PENERIMAAN PEMBELIAN', d.no_penerimaan, d.tanggal, d.status);

      let y = drawSupplierBlock(ctx, d, 55, 'DATA SUPPLIER');

      // === ITEMS ===
      doc.setFillColor(...primary);
      doc.rect(margin, y, 3, 8, 'F');
      doc.setTextColor(...dark);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('DETAIL BARANG DITERIMA', margin + 7, y + 6);
      y += 14;

      const headers = [['No', 'Kode', 'Nama Sparepart', 'Dipesan', 'Diterima', 'Harga Satuan', 'Subtotal']];
      const rows = (d.items || []).map((it, i) => [
        (i + 1).toString(), it.code || '-', it.name || '-',
        (it.qty_dipesan || 0).toString(), it.qty_diterima.toString(),
        fmtRupiah(it.harga_satuan), fmtRupiah(it.subtotal),
      ]);
      const dppItems = (d.items || []).reduce((s, it) => s + Number(it.subtotal || 0), 0);
      rows.push(['', '', 'SUBTOTAL', (d.total_qty || 0).toString(), '', '', fmtRupiah(dppItems)]);

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: headers,
        body: rows,
        styles: { fontSize: 8, cellPadding: 3, lineColor: [220, 220, 220], lineWidth: 0.4, textColor: [50, 50, 50] },
        headStyles: { fillColor: primary, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7, halign: 'center' },
        columnStyles: {
          0: { halign: 'center', cellWidth: 12 },
          1: { cellWidth: 25, fontStyle: 'bold' },
          3: { halign: 'center', cellWidth: 18 },
          4: { halign: 'center', cellWidth: 18 },
          5: { halign: 'right', cellWidth: 32 },
          6: { halign: 'right', cellWidth: 38, fontStyle: 'bold' },
        },
        footStyles: { fillColor: lightBg, textColor: dark, fontStyle: 'bold', fontSize: 8 },
      });

      y = doc.lastAutoTable.finalY + 8;
      const diskonFaktur = Number(d.diskon_rupiah) > 0 ? Number(d.diskon_rupiah) : dppItems * (Number(d.diskon_persen) || 0) / 100;
      const ppnVal = Number(d.ppn) || 0;
      const biayaVal = Number(d.biaya_lain) || 0;
      if (diskonFaktur > 0 || ppnVal > 0 || biayaVal > 0) {
        doc.setTextColor(...dark);
        doc.setFontSize(9);
        if (diskonFaktur > 0) {
          doc.setFont('helvetica', 'normal');
          doc.text(`Diskon Faktur${Number(d.diskon_persen) > 0 ? ` (${d.diskon_persen}%)` : ''}`, margin, y);
          doc.text('-' + fmtRupiah(diskonFaktur), pageW - margin - 12, y, { align: 'right' });
          y += 7;
        }
        doc.setFont('helvetica', 'normal');
        doc.text('DPP (setelah diskon)', margin, y);
        doc.text(fmtRupiah(dppItems - diskonFaktur), pageW - margin - 12, y, { align: 'right' });
        y += 7;
        if (ppnVal > 0) {
          doc.setFont('helvetica', 'bold');
          doc.text(`PPN (${Number(d.ppn_persen) || 11}%)`, margin, y);
          doc.text(fmtRupiah(ppnVal), pageW - margin - 12, y, { align: 'right' });
          y += 7;
        }
        if (biayaVal > 0) {
          doc.setFont('helvetica', 'bold');
          doc.text(`Biaya Lain${d.biaya_lain_catatan ? ` (${d.biaya_lain_catatan})` : ''}`, margin, y);
          doc.text(fmtRupiah(biayaVal), pageW - margin - 12, y, { align: 'right' });
          y += 7;
        }
        y += 2;
      }
      doc.setFillColor(...primary);
      doc.roundedRect(margin, y, contentW, 14, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('GRAND TOTAL', margin + 12, y + 6);
      doc.text(fmtRupiah(d.total_amount), pageW - margin - 12, y + 6, { align: 'right' });
      y += 20;

      // Metode pembayaran
      if (d.metode_pembayaran) {
        doc.setTextColor(...dark);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(`Metode Pembayaran: ${d.metode_pembayaran}`, margin, y);
        y += 8;
      }

      if (d.notes) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...gray);
        const noteLines = doc.splitTextToSize(d.notes, contentW);
        doc.text(noteLines, margin, y);
      }

      // Footer
      const footerY = pageH - 40;
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, footerY, pageW - margin, footerY);
      const now = new Date();
      doc.setTextColor(...gray);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text(`Dicetak pada: ${now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, margin, footerY + 5);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text('Diterima oleh,', margin, footerY - 25);
      doc.text('( ........................... )', margin, footerY - 8);
      doc.text('Diketahui oleh,', pageW - margin - 60, footerY - 25);
      doc.text('( ........................... )', pageW - margin - 60, footerY - 8);

      doc.save(`Penerimaan_${d.no_penerimaan || d.id}.pdf`);
    } catch (err) {
      console.error('Gagal generate PDF penerimaan', err);
      alert('Gagal mencetak penerimaan');
    }
  };

  const generateFakturPDF = async (item) => {
    if (!item) return;
    try {
      const res = await getFakturPembelianById(item.id);
      const d = res.data;
      if (!d) return;
      const ctx = buildDoc();
      const { doc, pageW, pageH, margin, contentW, primary, dark, gray, lightBg, fmtRupiah, autoTable } = ctx;
      drawHeader(ctx, 'FAKTUR PEMBELIAN', d.no_faktur, d.tanggal, d.status);

      let y = drawSupplierBlock(ctx, d, 55, 'DATA SUPPLIER');

      doc.setFillColor(...primary);
      doc.rect(margin, y, 3, 8, 'F');
      doc.setTextColor(...dark);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('DETAIL BARANG', margin + 7, y + 6);
      y += 14;

      const headers = [['No', 'Kode', 'Nama Sparepart', 'Qty', 'Harga Satuan', 'Subtotal']];
      const rows = (d.items || []).map((it, i) => [
        (i + 1).toString(), it.code || '-', it.name || '-', it.qty.toString(),
        fmtRupiah(it.harga_satuan), fmtRupiah(it.subtotal),
      ]);
      const ppnFaktur = Number(d.ppn) || 0;
      const biayaFaktur = Number(d.biaya_lain) || 0;
      const dppFaktur = Number(d.total_amount) - ppnFaktur - biayaFaktur;
      rows.push(['', '', 'SUBTOTAL', (d.total_qty || 0).toString(), '', fmtRupiah(dppFaktur)]);

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: headers,
        body: rows,
        styles: { fontSize: 8, cellPadding: 3, lineColor: [220, 220, 220], lineWidth: 0.4, textColor: [50, 50, 50] },
        headStyles: { fillColor: primary, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7, halign: 'center' },
        columnStyles: {
          0: { halign: 'center', cellWidth: 12 },
          1: { cellWidth: 25, fontStyle: 'bold' },
          3: { halign: 'center', cellWidth: 18 },
          4: { halign: 'right', cellWidth: 35 },
          5: { halign: 'right', cellWidth: 40, fontStyle: 'bold' },
        },
        footStyles: { fillColor: lightBg, textColor: dark, fontStyle: 'bold', fontSize: 8 },
      });

      y = doc.lastAutoTable.finalY + 8;
      if (ppnFaktur > 0 || biayaFaktur > 0) {
        doc.setTextColor(...dark);
        doc.setFontSize(9);
        if (ppnFaktur > 0) {
          doc.setFont('helvetica', 'normal');
          doc.text('DPP (setelah diskon)', margin, y);
          doc.text(fmtRupiah(dppFaktur), pageW - margin - 12, y, { align: 'right' });
          y += 7;
          doc.setFont('helvetica', 'bold');
          doc.text(`PPN (${Number(d.ppn_persen) || 11}%)`, margin, y);
          doc.text(fmtRupiah(ppnFaktur), pageW - margin - 12, y, { align: 'right' });
          y += 7;
        }
        if (biayaFaktur > 0) {
          doc.setFont('helvetica', 'bold');
          doc.text(`Biaya Lain${d.biaya_lain_catatan ? ` (${d.biaya_lain_catatan})` : ''}`, margin, y);
          doc.text(fmtRupiah(biayaFaktur), pageW - margin - 12, y, { align: 'right' });
          y += 7;
        }
        y += 2;
      }
      doc.setFillColor(...primary);
      doc.roundedRect(margin, y, contentW, 14, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('TOTAL FAKTUR', margin + 12, y + 6);
      doc.text(fmtRupiah(d.total_amount), pageW - margin - 12, y + 6, { align: 'right' });
      y += 20;

      if (d.notes) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...gray);
        const noteLines = doc.splitTextToSize(d.notes, contentW);
        doc.text(noteLines, margin, y);
      }

      const footerY = pageH - 40;
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, footerY, pageW - margin, footerY);
      const now = new Date();
      doc.setTextColor(...gray);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text(`Dicetak pada: ${now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, margin, footerY + 5);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text('Disetujui oleh,', margin, footerY - 25);
      doc.text('( ........................... )', margin, footerY - 8);
      doc.text('Dibuat oleh,', pageW - margin - 60, footerY - 25);
      doc.text('( ........................... )', pageW - margin - 60, footerY - 8);

      doc.save(`Faktur_${d.no_faktur || d.id}.pdf`);
    } catch (err) {
      console.error('Gagal generate PDF faktur', err);
      alert('Gagal mencetak faktur');
    }
  };

  const generatePurchaseOrderPDF = async (item) => {
    if (!item) return;
    try {
      const res = await getPurchaseOrderById(item.id);
      const d = res.data;
      if (!d) return;
      const ctx = buildDoc();
      const { doc, pageW, pageH, margin, contentW, primary, dark, gray, lightBg, fmtRupiah, autoTable } = ctx;
      drawHeader(ctx, 'PURCHASE ORDER', d.no_po, d.tanggal, d.status);

      let y = drawSupplierBlock(ctx, d, 55, 'DATA SUPPLIER');

      doc.setFillColor(...primary);
      doc.rect(margin, y, 3, 8, 'F');
      doc.setTextColor(...dark);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('DETAIL ITEM PESANAN', margin + 7, y + 6);
      y += 14;

      const headers = [['No', 'Kode', 'Nama Sparepart', 'Satuan', 'Qty', 'Harga Satuan', 'Subtotal']];
      const rows = (d.items || []).map((it, i) => [
        (i + 1).toString(), it.code || '-', it.name || '-', it.unit || '-', it.qty.toString(),
        fmtRupiah(it.harga_satuan), fmtRupiah(it.subtotal),
      ]);
      const ppnPO = Number(d.ppn) || 0;
      const dppPO = Number(d.total_amount) - ppnPO;
      rows.push(['', '', 'SUBTOTAL', '', (d.total_qty || 0).toString(), '', fmtRupiah(dppPO)]);

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: headers,
        body: rows,
        styles: { fontSize: 8, cellPadding: 3, lineColor: [220, 220, 220], lineWidth: 0.4, textColor: [50, 50, 50] },
        headStyles: { fillColor: primary, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7, halign: 'center' },
        columnStyles: {
          0: { halign: 'center', cellWidth: 12 },
          1: { cellWidth: 24, fontStyle: 'bold' },
          3: { halign: 'center', cellWidth: 18 },
          4: { halign: 'center', cellWidth: 16 },
          5: { halign: 'right', cellWidth: 32 },
          6: { halign: 'right', cellWidth: 38, fontStyle: 'bold' },
        },
        footStyles: { fillColor: lightBg, textColor: dark, fontStyle: 'bold', fontSize: 8 },
      });

      y = doc.lastAutoTable.finalY + 8;
      if (ppnPO > 0) {
        doc.setTextColor(...dark);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(`PPN (${Number(d.ppn_persen) || 11}%)`, margin, y);
        doc.text(fmtRupiah(ppnPO), pageW - margin - 12, y, { align: 'right' });
        y += 8;
      }
      doc.setFillColor(...primary);
      doc.roundedRect(margin, y, contentW, 14, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('GRAND TOTAL', margin + 12, y + 6);
      doc.text(fmtRupiah(d.total_amount), pageW - margin - 12, y + 6, { align: 'right' });
      y += 20;

      if (d.notes) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...gray);
        const noteLines = doc.splitTextToSize(d.notes, contentW);
        doc.text(noteLines, margin, y);
      }

      const footerY = pageH - 40;
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, footerY, pageW - margin, footerY);
      const now = new Date();
      doc.setTextColor(...gray);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text(`Dicetak pada: ${now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, margin, footerY + 5);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text('Dibuat oleh,', margin, footerY - 25);
      doc.text('( ........................... )', margin, footerY - 8);
      doc.text('Disetujui oleh,', pageW - margin - 60, footerY - 25);
      doc.text('( ........................... )', pageW - margin - 60, footerY - 8);

      doc.save(`Nota_PO_${d.no_po || d.id}.pdf`);
    } catch (err) {
      console.error('Gagal generate PDF PO', err);
      alert('Gagal mencetak Nota PO');
    }
  };

  // ============ BADGE HELPERS ============
  const badgeStatus = (s) => {
    if (s === 'Selesai' || s === 'Lunas' || s === 'Diverifikasi' || s === 'Disetujui') return 'badge-success';
    if (s === 'Dikirim' || s === 'Pending' || s === 'Belum Dibayar') return 'badge-warning';
    if (s === 'Dibatalkan' || s === 'Ditolak') return 'badge-danger';
    return 'badge-default';
  };

  const statusLabel = (s) => {
    if (s === 'Diverifikasi') return 'Disetujui';
    return s;
  };

  // Warna tab aktif untuk header tabel
  const tabColor = (TABS.find(t => t.key === activeTab) || TABS[0]).color;
  const detailTabColor = detail?.kind === 'penerimaan' ? '#2dce89' : detail?.kind === 'faktur' ? '#fb6340' : '#5e72e4';

  // ============ RENDER ============
  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Purchase Order</h2>
            <p className="page-desc">
              {activeTab === 'po' && `${data.length} purchase order`}
              {activeTab === 'verifikasi' && `${filteredVerif.length} PO menunggu terima barang`}
              {activeTab === 'pending' && `${filteredPending.length} PO menunggu keputusan`}
              {activeTab === 'penerimaan' && `${penerimaan.length} penerimaan pembelian`}
              {activeTab === 'faktur' && `${faktur.length} faktur pembelian`}
            </p>
          </div>
          {activeTab === 'po' && hasMenuAction('inventori', 'create') && (
            <button className="btn btn-primary" onClick={openModal}>
              <Plus size={16} /><span>Buat PO</span>
            </button>
          )}
          {activeTab === 'faktur' && hasMenuAction('inventori', 'create') && (
            <button className="btn btn-primary" onClick={openFakturCreate}>
              <Plus size={16} /><span>Buat Faktur</span>
            </button>
          )}
          {activeTab === 'penerimaan' && (
            <button className="btn btn-secondary" onClick={load}>
              <RefreshCw size={16} /><span>Muat Ulang</span>
            </button>
          )}
        </div>
      </div>

      {/* TAB NAVIGATION */}
      <div style={{
        display: 'flex', gap: 0, marginBottom: 16,
        background: '#fff', borderRadius: 10,
        border: '1px solid var(--gray-100)',
        overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        {TABS.map(tab => {
          const count = tab.key === 'po' ? filteredPO.length
            : tab.key === 'verifikasi' ? filteredVerif.length
            : tab.key === 'pending' ? filteredPending.length
            : tab.key === 'ditolak' ? filteredDitolak.length
            : tab.key === 'penerimaan' ? penerimaan.length
            : faktur.length;
          const active = activeTab === tab.key;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              flex: 1, padding: '12px 16px', border: 'none', cursor: 'pointer', position: 'relative',
              background: active
                ? `linear-gradient(135deg, ${tab.color}1c, ${tab.color}0c)`
                : `linear-gradient(135deg, ${tab.color}0d, ${tab.color}05)`,
              borderBottom: `3px solid ${active ? tab.color : tab.color + '55'}`,
              transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: 10,
            }}
              onMouseEnter={e => { e.currentTarget.style.background = `linear-gradient(135deg, ${tab.color}20, ${tab.color}10)`; }}
              onMouseLeave={e => { e.currentTarget.style.background = active
                ? `linear-gradient(135deg, ${tab.color}1c, ${tab.color}0c)`
                : `linear-gradient(135deg, ${tab.color}0d, ${tab.color}05)`; }}>
              <div style={{
                width: 34, height: 34, borderRadius: 10,
                background: tab.color + '20',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s',
              }}>
                <tab.icon size={17} color={tab.color} />
              </div>
              <div style={{ textAlign: 'left', flex: 1 }}>
                <div style={{
                  fontSize: '0.82rem', fontWeight: active ? 700 : 600,
                  color: active ? tab.color : 'var(--gray-600)',
                  transition: 'color 0.2s',
                }}>{tab.label}</div>
                <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)', marginTop: 1 }}>{tab.desc}</div>
              </div>
              <div style={{
                minWidth: 24, height: 22, borderRadius: 11,
                background: active ? tab.color : tab.color + '22',
                color: active ? '#fff' : tab.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.7rem', fontWeight: 700, padding: '0 7px',
              }}>{count}</div>
            </button>
          );
        })}
      </div>

      {/* STAT CARDS — count per tab */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 16 }}>
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid var(--gray-100)', padding: '12px 16px', borderTop: `3px solid #5e72e4` }}>
          <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)', fontWeight: 600, textTransform: 'uppercase' }}>PO Draft</div>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#5e72e4' }}>{filteredPO.length}</div>
          <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)' }}>Siap diverifikasi</div>
        </div>
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid var(--gray-100)', padding: '12px 16px', borderTop: `3px solid #f5365c` }}>
          <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)', fontWeight: 600, textTransform: 'uppercase' }}>Verifikasi PO</div>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#f5365c' }}>{filteredVerif.length}</div>
          <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)' }}>{rupiah(verifNilai)}</div>
        </div>
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid var(--gray-100)', padding: '12px 16px', borderTop: `3px solid #f59e0b` }}>
          <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)', fontWeight: 600, textTransform: 'uppercase' }}>PO Pending</div>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#f59e0b' }}>{filteredPending.length}</div>
          <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)' }}>Menunggu keputusan</div>
        </div>
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid var(--gray-100)', padding: '12px 16px', borderTop: `3px solid #dc2626` }}>
          <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)', fontWeight: 600, textTransform: 'uppercase' }}>PO Ditolak</div>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#dc2626' }}>{filteredDitolak.length}</div>
          <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)' }}>Bisa diajukan ulang</div>
        </div>
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid var(--gray-100)', padding: '12px 16px', borderTop: `3px solid #2dce89` }}>
          <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)', fontWeight: 600, textTransform: 'uppercase' }}>Penerimaan</div>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#2dce89' }}>{filteredPenerimaan.length}</div>
          <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)' }}>{rupiah(terimaNilai)}</div>
        </div>
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid var(--gray-100)', padding: '12px 16px', borderTop: `3px solid #fb6340` }}>
          <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)', fontWeight: 600, textTransform: 'uppercase' }}>Faktur</div>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#fb6340' }}>{filteredFaktur.length}</div>
          <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)' }}>{rupiah(fakturBelum)} belum bayar</div>
        </div>
      </div>

      <div className="filters">
        <div className="search-box">
          <Search size={16} />
          <input type="text" placeholder={`Cari ${activeTab === 'po' || activeTab === 'verifikasi' || activeTab === 'pending' || activeTab === 'ditolak' ? 'PO' : activeTab === 'penerimaan' ? 'Penerimaan' : 'Faktur'}...`} value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>Tanggal:</span>
          <input type="date" value={tglMulai} onChange={e => setTglMulai(e.target.value)} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.8rem' }} />
          <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>s.d</span>
          <input type="date" value={tglAkhir} onChange={e => setTglAkhir(e.target.value)} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.8rem' }} />
          <button onClick={() => { setTglMulai(getCurrentMonthRange().awal); setTglAkhir(getCurrentMonthRange().akhir); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', fontSize: '0.75rem', cursor: 'pointer', color: '#475569' }}>Bulan Ini</button>
        </div>
      </div>

      {loading ? <div className="page-loading">Memuat data...</div> : (
        <>
          {/* ═══════════════ TAB PO ═══════════════ */}
          {activeTab === 'po' && (
            <div className="table-wrapper">
              <table className="data-table" style={{ '--tab-color': tabColor, '--tab-text': '#ffffff' }}>
                <thead>
                  <tr>
                    <th>No. PO</th><th>Tanggal</th><th>Supplier</th><th>Gudang</th><th>Total Qty</th><th>Grand Total</th><th>Status</th><th style={{ width: 180 }}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPO.map(d => (
                    <tr key={d.id}>
                      <td><strong style={{ fontSize: '0.8rem' }}>{d.no_po}</strong></td>
                      <td>{d.tanggal}</td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{d.supplier_name || '-'}</div>
                        {d.supplier_phone && <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{d.supplier_phone}</div>}
                      </td>
                      <td>{d.gudang_nama || '-'}</td>
                      <td>{d.total_qty}</td>
                      <td style={{ fontWeight: 600 }}>{rupiah(d.total_amount)}</td>
                      <td>
                        <span className={`badge ${badgeStatus(d.status)}`}>{statusLabel(d.status)}</span>
                        {(d.status === 'Diverifikasi' && (d.qty_diterima || 0) > 0 && (d.qty_diterima || 0) < d.total_qty) && (
                          <div style={{ fontSize: '0.68rem', color: '#b45309', marginTop: 4 }}>Baru diterima sebagian</div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 2, whiteSpace: 'nowrap' }}>
                          <button className="btn-icon" title="Lihat" onClick={() => openDetailPO(d)} style={{ color: '#64748b' }}><Eye size={14} /></button>
                          {d.status === 'Draft' && (
                            <>
                              <button className="btn-icon" title="Edit PO" onClick={() => openEdit(d)} style={{ color: '#11cdef' }}><Edit2 size={14} /></button>
                              <button className="btn-icon" title="Verifikasi Item" onClick={() => handleVerifikasi(d)} style={{ color: '#059669' }}><ShieldCheck size={14} /></button>
                            </>
                          )}
                          {d.status === 'Dikirim' && (
                            <>
                              <button className="btn-icon" title="Selesaikan PO" onClick={() => handleStatus(d.id, 'Selesai')} style={{ color: '#059669' }}><CheckCircle size={14} /></button>
                              <button className="btn-icon" title="Batalkan PO" onClick={() => handleStatus(d.id, 'Dibatalkan')} style={{ color: '#dc2626' }}><XCircle size={14} /></button>
                            </>
                          )}
                          {d.status === 'Draft' && hasMenuAction('inventori', 'delete') && (
                            <button className="btn-icon btn-danger" onClick={() => handleDeletePO(d.id)}><Trash2 size={14} /></button>
                          )}
                          <button className="btn-icon" title="Cetak Nota PO" onClick={() => generatePurchaseOrderPDF(d)} style={{ color: '#11cdef' }}><Printer size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredPO.length === 0 && <tr><td colSpan={8} className="text-center text-muted" style={{ padding: 40 }}>Belum ada Purchase Order</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {/* ═══════════════ TAB VERIFIKASI PO ═══════════════ */}
          {activeTab === 'verifikasi' && (
            <div className="table-wrapper">
              <table className="data-table table-fit" style={{ '--tab-color': tabColor, '--tab-text': '#ffffff' }}>
                <thead>
                  <tr>
                    <th style={{ width: '11%' }}>No. PO</th><th style={{ width: '7%' }}>Tanggal</th><th style={{ width: '19%' }}>Supplier</th><th style={{ width: '9%' }}>Gudang</th><th style={{ width: '8%' }}>Diterima</th><th style={{ width: '12%' }}>Grand Total</th><th style={{ width: '9%' }}>PPN</th><th style={{ width: '9%' }}>Status</th><th style={{ width: '16%' }}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVerif.map(d => {
                    const diterima = d.qty_diterima || 0;
                    const sebagian = diterima > 0 && diterima < d.total_qty;
                    return (
                      <tr key={d.id}>
                        <td className="nowrap-cell"><strong style={{ fontSize: '0.8rem' }}>{d.no_po}</strong></td>
                        <td className="nowrap-cell">{d.tanggal}</td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{d.supplier_name || '-'}</div>
                          {d.supplier_phone && <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{d.supplier_phone}</div>}
                        </td>
                        <td>{d.gudang_nama || '-'}</td>
                        <td>
                          <strong>{diterima} / {d.total_qty}</strong>
                          {sebagian && <div style={{ fontSize: '0.7rem', color: '#b45309', marginTop: 2 }}>Barang baru diterima sebagian</div>}
                        </td>
                        <td className="nowrap-cell" style={{ fontWeight: 600 }}>{rupiah(d.total_amount)}</td>
                        <td className="nowrap-cell">
                          {Number(d.ppn) > 0 ? (
                            <span style={{ fontWeight: 600, color: '#7c3aed' }}>{rupiah(d.ppn)}</span>
                          ) : (
                            <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>-</span>
                          )}
                        </td>
                        <td className="nowrap-cell">
                          <span className={`badge ${sebagian ? 'badge-warning' : 'badge-default'}`}>
                            {sebagian ? 'Diterima Sebagian' : d.status}
                          </span>
                        </td>
                        <td className="nowrap-cell">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <button className="btn-icon" title="Lihat" onClick={() => openDetailPO(d)} style={{ color: '#64748b' }}><Eye size={14} /></button>
                            <button className="btn-icon" title="Terima Barang" onClick={() => openTerima(d)} style={{ color: '#059669' }}><PackageCheck size={14} /></button>
                            <button className="btn-icon" title="Edit PO" onClick={() => openEdit(d)} style={{ color: '#11cdef' }}><Edit2 size={14} /></button>
                            {hasMenuAction('inventori', 'delete') && (
                              <button className="btn-icon btn-danger" onClick={() => handleDeletePO(d.id)}><Trash2 size={14} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredVerif.length === 0 && <tr><td colSpan={9} className="text-center text-muted" style={{ padding: 40 }}>Tidak ada PO yang menunggu verifikasi</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {/* ═══════════════ TAB PO PENDING ═══════════════ */}
          {activeTab === 'pending' && (
            <div className="table-wrapper">
              <table className="data-table" style={{ '--tab-color': tabColor, '--tab-text': '#ffffff' }}>
                <thead>
                  <tr>
                    <th>No. PO</th><th>Tanggal</th><th>Supplier</th><th>Gudang</th><th>Total Qty</th><th>Grand Total</th><th>Status</th><th style={{ width: 180 }}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPending.map(d => {
                    const adaItemPending = (d.pending_items || 0) > 0;
                    return (
                      <tr key={d.id}>
                        <td><strong style={{ fontSize: '0.8rem' }}>{d.no_po}</strong></td>
                        <td>{d.tanggal}</td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{d.supplier_name || '-'}</div>
                          {d.supplier_phone && <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{d.supplier_phone}</div>}
                        </td>
                        <td>{d.gudang_nama || '-'}</td>
                        <td>{d.total_qty}</td>
                        <td style={{ fontWeight: 600 }}>{rupiah(d.total_amount)}</td>
                        <td>
                          {d.status === 'Pending' ? (
                            <span className="badge badge-warning">Pending</span>
                          ) : (
                            <span className="badge badge-warning" title={`${d.pending_items} item menunggu keputusan`}>Ada Item Pending ({d.pending_items})</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 2, whiteSpace: 'nowrap' }}>
                            <button className="btn-icon" title="Lihat" onClick={() => openDetailPO(d)} style={{ color: '#64748b' }}><Eye size={14} /></button>
                            {adaItemPending && d.status === 'Diverifikasi' && (
                              <button className="btn-icon" title="Proses Item Pending" onClick={() => openDetailPO(d, { verifyPending: true })} style={{ color: '#f59e0b' }}><ShieldCheck size={14} /></button>
                            )}
                            {d.status === 'Pending' && (
                              <button className="btn-icon" title="Ajukan Kembali" onClick={() => handleAjukanKembali(d.id)} style={{ color: '#f59e0b' }}><RotateCcw size={14} /></button>
                            )}
                            {hasMenuAction('inventori', 'delete') && (
                              <button className="btn-icon btn-danger" onClick={() => handleDeletePO(d.id)}><Trash2 size={14} /></button>
                            )}
                            <button className="btn-icon" title="Cetak Nota PO" onClick={() => generatePurchaseOrderPDF(d)} style={{ color: '#11cdef' }}><Printer size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredPending.length === 0 && <tr><td colSpan={8} className="text-center text-muted" style={{ padding: 40 }}>Tidak ada PO yang menunggu keputusan</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {/* ═══════════════ TAB PO DITOLAK ═══════════════ */}
          {activeTab === 'ditolak' && (
            <div className="table-wrapper">
              <table className="data-table" style={{ '--tab-color': tabColor, '--tab-text': '#ffffff' }}>
                <thead>
                  <tr>
                    <th>No. PO</th><th>Tanggal</th><th>Supplier</th><th>Gudang</th><th>Total Qty</th><th>Grand Total</th><th>Status</th><th style={{ width: 160 }}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDitolak.map(d => (
                    <tr key={d.id}>
                      <td><strong style={{ fontSize: '0.8rem' }}>{d.no_po}</strong></td>
                      <td>{d.tanggal}</td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{d.supplier_name || '-'}</div>
                        {d.supplier_phone && <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{d.supplier_phone}</div>}
                      </td>
                      <td>{d.gudang_nama || '-'}</td>
                      <td>{d.total_qty}</td>
                      <td style={{ fontWeight: 600 }}>{rupiah(d.total_amount)}</td>
                      <td><span className="badge badge-danger">{statusLabel(d.status)}</span></td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 2, whiteSpace: 'nowrap' }}>
                          <button className="btn-icon" title="Lihat" onClick={() => openDetailPO(d)} style={{ color: '#64748b' }}><Eye size={14} /></button>
                          <button className="btn-icon" title="Ajukan Kembali" onClick={() => handleAjukanKembali(d.id)} style={{ color: '#f59e0b' }}><RotateCcw size={14} /></button>
                          {hasMenuAction('inventori', 'delete') && (
                            <button className="btn-icon btn-danger" onClick={() => handleDeletePO(d.id)}><Trash2 size={14} /></button>
                          )}
                          <button className="btn-icon" title="Cetak Nota PO" onClick={() => generatePurchaseOrderPDF(d)} style={{ color: '#11cdef' }}><Printer size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredDitolak.length === 0 && <tr><td colSpan={8} className="text-center text-muted" style={{ padding: 40 }}>Tidak ada PO yang ditolak</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {/* ═══════════════ TAB PENERIMAAN PEMBELIAN ═══════════════ */}
          {activeTab === 'penerimaan' && (
            <div className="table-wrapper">
              <table className="data-table" style={{ '--tab-color': tabColor, '--tab-text': '#ffffff' }}>
                <thead>
                  <tr>
                    <th>No. Penerimaan</th><th>No. PO</th><th>Tanggal</th><th>Supplier</th><th>Total Qty</th><th>Grand Total</th><th>PPN</th><th>Metode Bayar</th><th>Status</th><th style={{ width: 180 }}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPenerimaan.map(d => (
                    <tr key={d.id}>
                      <td><strong style={{ fontSize: '0.8rem' }}>{d.no_penerimaan}</strong></td>
                      <td>{d.no_po || '-'}</td>
                      <td>{d.tanggal}</td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{d.supplier_name || '-'}</div>
                        {d.supplier_phone && <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{d.supplier_phone}</div>}
                      </td>
                      <td>{d.total_qty}</td>
                      <td style={{ fontWeight: 600 }}>{rupiah(d.total_amount)}</td>
                      <td>{Number(d.ppn) > 0 ? <span style={{ fontWeight: 600, color: '#7c3aed' }}>{rupiah(d.ppn)}</span> : <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>-</span>}</td>
                      <td>
                        {d.metode_pembayaran ? (
                          <span className="badge badge-default" style={{ textTransform: 'none' }}>{d.metode_pembayaran}</span>
                        ) : (
                          <span style={{ fontSize: '0.72rem', color: '#b45309' }}>Belum dipilih</span>
                        )}
                      </td>
                      <td><span className={`badge ${badgeStatus(d.status)}`}>{d.status}</span></td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 2, whiteSpace: 'nowrap' }}>
                          <button className="btn-icon" title="Lihat" onClick={() => openDetailPenerimaan(d)} style={{ color: '#64748b' }}><Eye size={14} /></button>
                          <button className="btn-icon" title="Cetak" onClick={() => generatePenerimaanPDF(d)} style={{ color: '#11cdef' }}><Printer size={14} /></button>
                          <button className="btn-icon" title="Edit" onClick={() => openEditPenerimaan(d)} style={{ color: '#11cdef' }}><Edit2 size={14} /></button>
                          <button className="btn-icon" title="Metode Pembayaran (Jurnal Akuntansi)" onClick={() => openBayar(d)} style={{ color: '#7c3aed' }}><Wallet size={14} /></button>
                          {hasMenuAction('inventori', 'delete') && (
                            <button className="btn-icon btn-danger" onClick={() => handleDeletePenerimaan(d)}><Trash2 size={14} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredPenerimaan.length === 0 && <tr><td colSpan={10} className="text-center text-muted" style={{ padding: 40 }}>Belum ada penerimaan pembelian</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {/* ═══════════════ TAB FAKTUR PEMBELIAN ═══════════════ */}
          {activeTab === 'faktur' && (
            <div className="table-wrapper">
              <table className="data-table" style={{ '--tab-color': tabColor, '--tab-text': '#ffffff' }}>
                <thead>
                  <tr>
                    <th>No. Faktur</th><th>No. Penerimaan</th><th>No. PO</th><th>Tanggal</th><th>Supplier</th><th>Total Qty</th><th>Total</th><th>PPN</th><th>Status</th><th style={{ width: 150 }}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFaktur.map(d => (
                    <tr key={d.id}>
                      <td><strong style={{ fontSize: '0.8rem' }}>{d.no_faktur}</strong></td>
                      <td>{d.no_penerimaan || '-'}</td>
                      <td>{d.no_po || '-'}</td>
                      <td>{d.tanggal}</td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{d.supplier_name || '-'}</div>
                      </td>
                      <td>{d.total_qty}</td>
                      <td style={{ fontWeight: 600 }}>{rupiah(d.total_amount)}</td>
                      <td>{Number(d.ppn) > 0 ? <span style={{ fontWeight: 600, color: '#7c3aed' }}>{rupiah(d.ppn)}</span> : <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>-</span>}</td>
                      <td><span className={`badge ${badgeStatus(d.status)}`}>{d.status}</span></td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 2, whiteSpace: 'nowrap' }}>
                          <button className="btn-icon" title="Lihat" onClick={() => openDetailFaktur(d)} style={{ color: '#64748b' }}><Eye size={14} /></button>
                          <button className="btn-icon" title="Cetak" onClick={() => generateFakturPDF(d)} style={{ color: '#11cdef' }}><Printer size={14} /></button>
                          <button className="btn-icon" title="Edit" onClick={() => openFakturEdit(d)} style={{ color: '#11cdef' }}><Edit2 size={14} /></button>
                          {hasMenuAction('inventori', 'delete') && (
                            <button className="btn-icon btn-danger" onClick={() => handleDeleteFaktur(d)}><Trash2 size={14} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredFaktur.length === 0 && <tr><td colSpan={10} className="text-center text-muted" style={{ padding: 40 }}>Belum ada faktur pembelian</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ═══════════════ MODAL BUAT/EDIT PO ═══════════════ */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 800 }}>
            <div className="modal-header">
              <h3><ShoppingCart size={18} style={{ marginRight: 8 }} />{editing ? 'Edit Purchase Order' : 'Buat Purchase Order Baru'}</h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><span>{error}</span></div>}

              <div style={{ background: '#f0f4ff', borderRadius: 10, padding: '14px 16px', marginBottom: 16, border: '1px solid #d6e4ff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <Building2 size={16} style={{ color: '#4f6ef7' }} />
                  <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#2c3e6b', textTransform: 'uppercase', letterSpacing: 0.3 }}>Supplier</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group"><label>Supplier</label>
                    <SearchableSelect
                      options={suppliers.map(s => ({ id: s.id, code: s.kode || '', name: s.nama, telepon: s.telepon || '' }))}
                      value={suppliers.find(s => s.nama === form.supplier_name)?.id || ''}
                      onChange={v => handleSupplierSelect(v)}
                      placeholder="Ketik kode / nama supplier..."
                    />
                  </div>
                  <div className="form-group"><label>Telepon</label>
                    <input type="text" value={form.supplier_phone} onChange={e => setForm({ ...form, supplier_phone: e.target.value })} />
                  </div>
                </div>
                <div style={{ marginTop: 8 }}>
                  <div className="form-group"><label>Alamat</label>
                    <input type="text" value={form.supplier_address} onChange={e => setForm({ ...form, supplier_address: e.target.value })} />
                  </div>
                </div>
              </div>

              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', marginBottom: 16, border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <FileText size={16} style={{ color: '#475569' }} />
                  <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#334155', textTransform: 'uppercase', letterSpacing: 0.3 }}>Detail</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group"><label>Tanggal</label><input type="date" value={form.tanggal} onChange={e => setForm({ ...form, tanggal: e.target.value })} /></div>
                  <div className="form-group"><label>Gudang</label>
                    <select value={form.gudang_id} onChange={e => setForm({ ...form, gudang_id: e.target.value })}>
                      <option value="">-- Pilih Gudang --</option>
                      {gudangList.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label>Dibuat Oleh</label>
                    <input type="text" value={form.dibuat_oleh || user?.full_name || user?.username || '-'} readOnly style={{ background: '#eef2f6', cursor: 'not-allowed' }} />
                  </div>
                  <div className="form-group"><label>Diperiksa Oleh</label>
                    <input type="text" value={form.diperiksa_oleh} onChange={e => setForm({ ...form, diperiksa_oleh: e.target.value })} placeholder="Ketik nama pemeriksa" />
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}><label>Catatan</label>
                    <input type="text" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Catatan (opsional)" />
                  </div>
                </div>
              </div>

              <div style={{ background: '#fafbfc', borderRadius: 10, border: '1px solid #e8ecf0', padding: '12px 14px', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                  <Package size={15} style={{ color: '#475569' }} />
                  <span style={{ fontWeight: 600, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 0.3, color: '#334155' }}>
                    Item Pesanan <span style={{ fontSize: '0.75rem', color: '#94a3b8', background: '#eef2f6', padding: '1px 8px', borderRadius: 10, marginLeft: 6 }}>{form.items.length} item</span>
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '36px 2.6fr 60px 70px 120px 28px', gap: 6, padding: '6px 8px', fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', background: '#eef2f6', borderRadius: 6, marginBottom: 6 }}>
                  <div>No</div><div>Sparepart</div><div>Satuan</div><div style={{ textAlign: 'right' }}>Qty</div><div style={{ textAlign: 'right' }}>Harga</div><div></div>
                </div>

                {form.items.map((item, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '36px 2.6fr 60px 70px 120px 28px', gap: 6, padding: '5px 8px', alignItems: 'center', borderBottom: idx < form.items.length - 1 ? '1px solid #eef2f6' : 'none', background: idx % 2 === 0 ? 'white' : '#f8fafc' }}>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', textAlign: 'center' }}>{idx + 1}</div>
                    <div>
                      <SearchableSelect
                        options={spareparts.map(s => ({ id: s.id, code: s.code || '', name: s.name, stock: s.stock }))}
                        value={item.sparepart_id}
                        onChange={spId => {
                          const s = sparepartMap[spId];
                          const items = [...form.items];
                          items[idx] = { ...items[idx], sparepart_id: spId, unit: s ? (s.unit || '') : '', harga_satuan: s ? Number(s.harga_beli || s.price || 0) : 0 };
                          setForm({ ...form, items });
                        }}
                        placeholder="Ketik kode / nama item..."
                      />
                    </div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155', textAlign: 'center' }}>{item.unit || '-'}</div>
                    <div><input type="number" value={item.qty} onChange={e => updItem(idx, 'qty', e.target.value)} min="1" style={{ width: '100%', padding: '6px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.8rem', textAlign: 'right' }} /></div>
                    <div><input type="number" value={item.harga_satuan} onChange={e => updItem(idx, 'harga_satuan', e.target.value)} min="0" style={{ width: '100%', padding: '6px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.8rem', textAlign: 'right' }} /></div>
                    <div><button className="btn-icon btn-danger" onClick={() => delItem(idx)} disabled={form.items.length === 1} style={{ opacity: form.items.length === 1 ? 0.3 : 1 }}><X size={13} /></button></div>
                  </div>
                ))}

                <button onClick={addItem} style={{ marginTop: 10, padding: '7px 14px', fontSize: '0.8rem', background: 'white', border: '1px dashed #94a3b8', borderRadius: 6, cursor: 'pointer', color: '#475569', display: 'flex', alignItems: 'center', gap: 6, width: '100%', justifyContent: 'center' }}
                  onMouseEnter={e => { e.target.style.background = '#f1f5f9'; e.target.style.borderColor = '#64748b'; }}
                  onMouseLeave={e => { e.target.style.background = 'white'; e.target.style.borderColor = '#94a3b8'; }}
                ><Plus size={14} /> Tambah Item</button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12, padding: '10px 12px', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: '#5b21b6' }}>
                  <input type="checkbox" checked={!!form.ppn_aktif} onChange={e => setForm({ ...form, ppn_aktif: e.target.checked })} style={{ width: 16, height: 16, accentColor: '#7c3aed' }} />
                  PPN (Pajak Pertambahan Nilai)
                </label>
                {!!form.ppn_aktif && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="number" value={form.ppn_persen} onChange={e => setForm({ ...form, ppn_persen: e.target.value })} min="0" max="100" style={{ width: 70, padding: '6px 8px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.8rem', textAlign: 'right' }} />
                    <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>%</span>
                  </div>
                )}
                <div style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#7c3aed' }}>
                  {!!form.ppn_aktif ? 'PPN dihitung dari subtotal item' : 'Aktifkan bila supplier PKP (kena PPN)'}
                </div>
              </div>

              <div style={{ background: 'linear-gradient(135deg, #f0f9ff, #e6f7ff)', borderRadius: 10, padding: '12px 18px', border: '1px solid #bae6fd' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500, textTransform: 'uppercase' }}>Total Item</span>
                    <div style={{ fontSize: '1rem', fontWeight: 700, color: '#075985' }}>{form.items.length}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500, textTransform: 'uppercase' }}>Total Qty</span>
                    <div style={{ fontSize: '1rem', fontWeight: 700, color: '#075985' }}>{form.items.reduce((s, i) => s + (Number(i.qty) || 0), 0)}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500, textTransform: 'uppercase' }}>Grand Total</span>
                    <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0369a1' }}>
                      {(() => {
                        const sub = form.items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.harga_satuan) || 0), 0);
                        const ppn = !!form.ppn_aktif ? sub * ((Number(form.ppn_persen) || 11) / 100) : 0;
                        return rupiah(sub + ppn);
                      })()}
                    </div>
                    {!!form.ppn_aktif && (
                      <div style={{ fontSize: '0.68rem', color: '#7c3aed', fontWeight: 600 }}>termasuk PPN {Number(form.ppn_persen) || 11}%</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan...' : editing ? <><Package size={16} /> Simpan</> : <><Truck size={16} /> Buat PO</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ MODAL TERIMA BARANG ═══════════════ */}
      {terima && (
        <div className="modal-overlay" onClick={() => setTerima(null)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 920 }}>
            <div className="modal-header">
              <h3><PackageCheck size={18} style={{ marginRight: 8 }} />Terima Barang — {terima.po.no_po}</h3>
              <button className="btn-icon" onClick={() => setTerima(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><span>{error}</span></div>}

              <div style={{ background: '#f0f4ff', borderRadius: 10, padding: '12px 16px', marginBottom: 14, border: '1px solid #d6e4ff', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><span style={{ fontSize: '0.7rem', color: '#64748b' }}>Supplier</span><div style={{ fontWeight: 600 }}>{terima.po.supplier_name || '-'}</div></div>
                <div><span style={{ fontSize: '0.7rem', color: '#64748b' }}>Gudang PO</span><div style={{ fontWeight: 600 }}>{terima.po.gudang_nama || '-'}</div></div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                <div className="form-group"><label>Tanggal Terima</label>
                  <input type="date" value={terima.form.tanggal} onChange={e => setTerima({ ...terima, form: { ...terima.form, tanggal: e.target.value } })} />
                </div>
                <div className="form-group"><label>Gudang Tujuan</label>
                  <select value={terima.form.gudang_id} onChange={e => setTerima({ ...terima, form: { ...terima.form, gudang_id: e.target.value } })}>
                    <option value="">-- Pilih Gudang --</option>
                    {gudangList.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Metode Pembayaran</label>
                  <select value={terima.form.metode_pembayaran} onChange={e => updTerimaForm('metode_pembayaran', e.target.value)}>
                    <option value="">-- Pilih Metode --</option>
                    {METODE_LIST.map(m => <option key={m.key} value={m.key}>{m.label} — {m.desc}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Catatan</label>
                  <input type="text" value={terima.form.notes} onChange={e => setTerima({ ...terima, form: { ...terima.form, notes: e.target.value } })} placeholder="Catatan (opsional)" />
                </div>
              </div>

              <div style={{ background: '#fafbfc', borderRadius: 10, border: '1px solid #e8ecf0', padding: '12px 14px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 48px 62px 62px 74px 94px 58px', gap: 6, padding: '6px 8px', fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', background: '#eef2f6', borderRadius: 6, marginBottom: 6 }}>
                  <div>Sparepart</div><div style={{ textAlign: 'center' }}>Dipesan</div><div style={{ textAlign: 'center' }}>Diterima</div><div style={{ textAlign: 'center' }}>Sisa</div><div style={{ textAlign: 'right' }}>Terima</div><div style={{ textAlign: 'right' }}>Harga Beli</div><div style={{ textAlign: 'center' }}>Diskon %</div>
                </div>
                {terima.form.items.map((it, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 48px 62px 62px 74px 94px 58px', gap: 6, padding: '6px 8px', alignItems: 'center', borderBottom: idx < terima.form.items.length - 1 ? '1px solid #eef2f6' : 'none' }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '0.82rem' }}>{it.name || '-'}</div>
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{it.code}</div>
                    </div>
                    <div style={{ textAlign: 'center', fontSize: '0.82rem' }}>{it.qty_dipesan}</div>
                    <div style={{ textAlign: 'center', fontSize: '0.82rem', color: '#059669' }}>{it.qty_dipesan - it.sisa}</div>
                    <div style={{ textAlign: 'center', fontSize: '0.82rem', fontWeight: 600, color: it.sisa > 0 ? '#b45309' : '#059669' }}>{it.sisa}</div>
                    <div>
                      <input type="number" value={it.qty_diterima} onChange={e => updTerimaItem(idx, e.target.value)} min="0" max={it.sisa} style={{ width: '100%', padding: '6px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.8rem', textAlign: 'right' }} />
                    </div>
                    <div>
                      <input type="number" value={it.harga_satuan} onChange={e => updTerimaItemHarga(idx, e.target.value)} min="0" title="Harga beli saat barang datang — bisa diubah jika ada kenaikan/penurunan dari harga PO" style={{ width: '100%', padding: '6px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.8rem', textAlign: 'right', background: '#fff7ed', borderColor: '#fdba74' }} />
                    </div>
                    <div>
                      <input type="number" value={it.diskon_persen} onChange={e => updTerimaItemDiskon(idx, e.target.value)} min="0" max="100" style={{ width: '100%', padding: '6px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.8rem', textAlign: 'right' }} />
                    </div>
                  </div>
                ))}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14, paddingTop: 12, borderTop: '1px dashed #d8dee6' }}>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <div className="form-group" style={{ margin: 0 }}><label>Diskon Faktur (%)</label>
                      <input type="number" value={terima.form.diskon_persen} onChange={e => updTerimaForm('diskon_persen', e.target.value)} min="0" max="100" style={{ padding: '7px 10px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}><label>Diskon Faktur (Rp)</label>
                      <input type="number" value={terima.form.diskon_rupiah} onChange={e => updTerimaForm('diskon_rupiah', e.target.value)} min="0" style={{ padding: '7px 10px' }} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <div className="form-group" style={{ margin: 0 }}><label>Biaya Lain-lain (Rp)</label>
                      <input type="number" value={terima.form.biaya_lain} onChange={e => updTerimaForm('biaya_lain', e.target.value)} min="0" style={{ padding: '7px 10px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}><label>Catatan Biaya Lain</label>
                      <input type="text" value={terima.form.biaya_lain_catatan} onChange={e => updTerimaForm('biaya_lain_catatan', e.target.value)} placeholder="mis. ongkir, packing" style={{ padding: '7px 10px' }} />
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 12, padding: '10px 12px', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: '#5b21b6' }}>
                    <input type="checkbox" checked={!!terima.form.ppn_aktif} onChange={e => updTerimaForm('ppn_aktif', e.target.checked)} style={{ width: 16, height: 16, accentColor: '#7c3aed' }} />
                    PPN (Pajak Pertambahan Nilai)
                  </label>
                  {!!terima.form.ppn_aktif && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" value={terima.form.ppn_persen} onChange={e => updTerimaForm('ppn_persen', e.target.value)} min="0" max="100" style={{ width: 70, padding: '6px 8px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.8rem', textAlign: 'right' }} />
                      <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>%</span>
                    </div>
                  )}
                  <div style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#7c3aed' }}>
                    {!!terima.form.ppn_aktif ? 'PPN dihitung dari DPP (setelah diskon)' : 'Aktifkan bila supplier mengenakan PPN'}
                  </div>
                </div>

                {(() => {
                  const subtotal = terima.form.items.reduce((s, i) => s + (Number(i.qty_diterima) || 0) * (Number(i.harga_satuan) || 0), 0);
                  const diskonItem = terima.form.items.reduce((s, i) => s + (Number(i.qty_diterima) || 0) * (Number(i.harga_satuan) || 0) * (Number(i.diskon_persen) || 0) / 100, 0);
                  const setelahItem = subtotal - diskonItem;
                  const diskonFaktur = Number(terima.form.diskon_persen) > 0 ? setelahItem * (Number(terima.form.diskon_persen) / 100) : (Number(terima.form.diskon_rupiah) || 0);
                  const biayaLain = Number(terima.form.biaya_lain) || 0;
                  const dpp = setelahItem - diskonFaktur;
                  const ppnPersen = Number(terima.form.ppn_persen) || 11;
                  const ppn = !!terima.form.ppn_aktif ? dpp * ppnPersen / 100 : 0;
                  const grandTotal = dpp + ppn + biayaLain;
                  return (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, gap: 24 }}>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Subtotal</span>
                        <div style={{ fontWeight: 600 }}>{rupiah(subtotal)}</div>
                      </div>
                      {diskonItem > 0 && (
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Diskon Item</span>
                          <div style={{ fontWeight: 600, color: '#dc2626' }}>-{rupiah(diskonItem)}</div>
                        </div>
                      )}
                      {diskonFaktur > 0 && (
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Diskon Faktur</span>
                          <div style={{ fontWeight: 600, color: '#dc2626' }}>-{rupiah(diskonFaktur)}</div>
                        </div>
                      )}
                      {biayaLain > 0 && (
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Biaya Lain</span>
                          <div style={{ fontWeight: 600, color: '#b45309' }}>+{rupiah(biayaLain)}</div>
                        </div>
                      )}
                      {ppn > 0 && (
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '0.7rem', color: '#64748b' }}>PPN ({ppnPersen}%)</span>
                          <div style={{ fontWeight: 600, color: '#7c3aed' }}>+{rupiah(ppn)}</div>
                        </div>
                      )}
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Total</span>
                        <div style={{ fontWeight: 800, color: '#075985', fontSize: '1.05rem' }}>{rupiah(grandTotal)}</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setTerima(null)}>Batal</button>
              <button className="btn btn-primary" onClick={handleTerima} disabled={saving}>
                {saving ? 'Menyimpan...' : <><PackageCheck size={16} /> Terima Barang</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ MODAL EDIT PENERIMAAN ═══════════════ */}
      {editTerima && (
        <div className="modal-overlay" onClick={() => setEditTerima(null)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 920 }}>
            <div className="modal-header">
              <h3><PackageCheck size={18} style={{ marginRight: 8 }} />Edit Penerimaan — {editTerima.header.no_penerimaan}</h3>
              <button className="btn-icon" onClick={() => setEditTerima(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><span>{error}</span></div>}

              <div style={{ background: '#f0f4ff', borderRadius: 10, padding: '12px 16px', marginBottom: 14, border: '1px solid #d6e4ff', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><span style={{ fontSize: '0.7rem', color: '#64748b' }}>Supplier</span><div style={{ fontWeight: 600 }}>{editTerima.header.supplier_name || '-'}</div></div>
                <div><span style={{ fontSize: '0.7rem', color: '#64748b' }}>No. PO</span><div style={{ fontWeight: 600 }}>{editTerima.header.no_po || '-'}</div></div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                <div className="form-group"><label>Tanggal Terima</label>
                  <input type="date" value={editTerima.form.tanggal} onChange={e => setEditTerima({ ...editTerima, form: { ...editTerima.form, tanggal: e.target.value } })} />
                </div>
                <div className="form-group"><label>Gudang Tujuan</label>
                  <select value={editTerima.form.gudang_id} onChange={e => setEditTerima({ ...editTerima, form: { ...editTerima.form, gudang_id: e.target.value } })}>
                    <option value="">-- Pilih Gudang --</option>
                    {gudangList.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Metode Pembayaran</label>
                  <select value={editTerima.form.metode_pembayaran} onChange={e => updEditTerimaForm('metode_pembayaran', e.target.value)}>
                    <option value="">-- Pilih Metode --</option>
                    {METODE_LIST.map(m => <option key={m.key} value={m.key}>{m.label} — {m.desc}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Catatan</label>
                  <input type="text" value={editTerima.form.notes} onChange={e => setEditTerima({ ...editTerima, form: { ...editTerima.form, notes: e.target.value } })} placeholder="Catatan (opsional)" />
                </div>
              </div>

              <div style={{ background: '#fafbfc', borderRadius: 10, border: '1px solid #e8ecf0', padding: '12px 14px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 56px 66px 66px 76px 94px 58px', gap: 6, padding: '6px 8px', fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', background: '#eef2f6', borderRadius: 6, marginBottom: 6 }}>
                  <div>Sparepart</div><div style={{ textAlign: 'center' }}>Dipesan</div><div style={{ textAlign: 'center' }}>Diterima</div><div style={{ textAlign: 'center' }}>Maks.</div><div style={{ textAlign: 'right' }}>Qty Baru</div><div style={{ textAlign: 'right' }}>Harga Beli</div><div style={{ textAlign: 'center' }}>Diskon %</div>
                </div>
                {editTerima.form.items.map((it, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 56px 66px 66px 76px 94px 58px', gap: 6, padding: '6px 8px', alignItems: 'center', borderBottom: idx < editTerima.form.items.length - 1 ? '1px solid #eef2f6' : 'none' }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '0.82rem' }}>{it.name || '-'}</div>
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{it.code}</div>
                    </div>
                    <div style={{ textAlign: 'center', fontSize: '0.82rem' }}>{it.qty_dipesan}</div>
                    <div style={{ textAlign: 'center', fontSize: '0.82rem', color: '#059669' }}>{it.qty_diterima}</div>
                    <div style={{ textAlign: 'center', fontSize: '0.82rem', fontWeight: 600, color: '#64748b' }}>{it.sisa}</div>
                    <div>
                      <input type="number" value={it.qty_diterima} onChange={e => updEditTerimaItem(idx, e.target.value)} min="0" max={it.sisa} style={{ width: '100%', padding: '6px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.8rem', textAlign: 'right' }} />
                    </div>
                    <div>
                      <input type="number" value={it.harga_satuan} onChange={e => updEditTerimaItemHarga(idx, e.target.value)} min="0" title="Harga beli saat barang datang — bisa diubah" style={{ width: '100%', padding: '6px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.8rem', textAlign: 'right', background: '#fff7ed', borderColor: '#fdba74' }} />
                    </div>
                    <div>
                      <input type="number" value={it.diskon_persen} onChange={e => updEditTerimaItemDiskon(idx, e.target.value)} min="0" max="100" style={{ width: '100%', padding: '6px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.8rem', textAlign: 'right' }} />
                    </div>
                  </div>
                ))}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14, paddingTop: 12, borderTop: '1px dashed #d8dee6' }}>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <div className="form-group" style={{ margin: 0 }}><label>Diskon Faktur (%)</label>
                      <input type="number" value={editTerima.form.diskon_persen} onChange={e => updEditTerimaForm('diskon_persen', e.target.value)} min="0" max="100" style={{ padding: '7px 10px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}><label>Diskon Faktur (Rp)</label>
                      <input type="number" value={editTerima.form.diskon_rupiah} onChange={e => updEditTerimaForm('diskon_rupiah', e.target.value)} min="0" style={{ padding: '7px 10px' }} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <div className="form-group" style={{ margin: 0 }}><label>Biaya Lain-lain (Rp)</label>
                      <input type="number" value={editTerima.form.biaya_lain} onChange={e => updEditTerimaForm('biaya_lain', e.target.value)} min="0" style={{ padding: '7px 10px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}><label>Catatan Biaya Lain</label>
                      <input type="text" value={editTerima.form.biaya_lain_catatan} onChange={e => updEditTerimaForm('biaya_lain_catatan', e.target.value)} placeholder="mis. ongkir, packing" style={{ padding: '7px 10px' }} />
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 12, padding: '10px 12px', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: '#5b21b6' }}>
                    <input type="checkbox" checked={!!editTerima.form.ppn_aktif} onChange={e => updEditTerimaForm('ppn_aktif', e.target.checked)} style={{ width: 16, height: 16, accentColor: '#7c3aed' }} />
                    PPN (Pajak Pertambahan Nilai)
                  </label>
                  {!!editTerima.form.ppn_aktif && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" value={editTerima.form.ppn_persen} onChange={e => updEditTerimaForm('ppn_persen', e.target.value)} min="0" max="100" style={{ width: 70, padding: '6px 8px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.8rem', textAlign: 'right' }} />
                      <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>%</span>
                    </div>
                  )}
                  <div style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#7c3aed' }}>
                    {!!editTerima.form.ppn_aktif ? 'PPN dihitung dari DPP (setelah diskon)' : 'Aktifkan bila supplier mengenakan PPN'}
                  </div>
                </div>

                {(() => {
                  const subtotal = editTerima.form.items.reduce((s, i) => s + (Number(i.qty_diterima) || 0) * (Number(i.harga_satuan) || 0), 0);
                  const diskonItem = editTerima.form.items.reduce((s, i) => s + (Number(i.qty_diterima) || 0) * (Number(i.harga_satuan) || 0) * (Number(i.diskon_persen) || 0) / 100, 0);
                  const setelahItem = subtotal - diskonItem;
                  const diskonFaktur = Number(editTerima.form.diskon_persen) > 0 ? setelahItem * (Number(editTerima.form.diskon_persen) / 100) : (Number(editTerima.form.diskon_rupiah) || 0);
                  const biayaLain = Number(editTerima.form.biaya_lain) || 0;
                  const dpp = setelahItem - diskonFaktur;
                  const ppnPersen = Number(editTerima.form.ppn_persen) || 11;
                  const ppn = !!editTerima.form.ppn_aktif ? dpp * ppnPersen / 100 : 0;
                  const grandTotal = dpp + ppn + biayaLain;
                  return (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, gap: 24 }}>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Subtotal</span>
                        <div style={{ fontWeight: 600 }}>{rupiah(subtotal)}</div>
                      </div>
                      {diskonItem > 0 && (
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Diskon Item</span>
                          <div style={{ fontWeight: 600, color: '#dc2626' }}>-{rupiah(diskonItem)}</div>
                        </div>
                      )}
                      {diskonFaktur > 0 && (
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Diskon Faktur</span>
                          <div style={{ fontWeight: 600, color: '#dc2626' }}>-{rupiah(diskonFaktur)}</div>
                        </div>
                      )}
                      {biayaLain > 0 && (
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Biaya Lain</span>
                          <div style={{ fontWeight: 600, color: '#b45309' }}>+{rupiah(biayaLain)}</div>
                        </div>
                      )}
                      {ppn > 0 && (
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '0.7rem', color: '#64748b' }}>PPN ({ppnPersen}%)</span>
                          <div style={{ fontWeight: 600, color: '#7c3aed' }}>+{rupiah(ppn)}</div>
                        </div>
                      )}
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Total</span>
                        <div style={{ fontWeight: 800, color: '#075985', fontSize: '1.05rem' }}>{rupiah(grandTotal)}</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditTerima(null)}>Batal</button>
              <button className="btn btn-primary" onClick={handleEditTerima} disabled={saving}>
                {saving ? 'Menyimpan...' : <><Package size={16} /> Simpan</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ MODAL METODE PEMBAYARAN ═══════════════ */}
      {bayar && (
        <div className="modal-overlay" onClick={() => setBayar(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h3><Wallet size={18} style={{ marginRight: 8 }} />Metode Pembayaran</h3>
              <button className="btn-icon" onClick={() => setBayar(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><span>{error}</span></div>}
              <div style={{ background: '#f0f4ff', borderRadius: 10, padding: '12px 16px', marginBottom: 14, border: '1px solid #d6e4ff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Penerimaan</div>
                    <div style={{ fontWeight: 700 }}>{bayar.no_penerimaan}</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{bayar.supplier_name || '-'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Total</div>
                    <div style={{ fontWeight: 800, color: '#075985' }}>{rupiah(bayar.total_amount)}</div>
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 8 }}>
                Pilih tipe bayar — akan membuat jurnal akuntansi otomatis:
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {METODE_LIST.map(m => (
                  <div key={m.key} onClick={() => setBayar({ ...bayar, metode: m.key })}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                      border: bayar.metode === m.key ? `2px solid #7c3aed` : '1px solid var(--gray-200)',
                      background: bayar.metode === m.key ? '#f5f3ff' : '#fff',
                    }}>
                    {m.key === 'Hutang' ? <CreditCard size={18} color="#7c3aed" /> : m.key === 'Transfer' ? <Truck size={18} color="#7c3aed" /> : <Banknote size={18} color="#7c3aed" />}
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{m.label}</div>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{m.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setBayar(null)}>Batal</button>
              <button className="btn btn-primary" onClick={handleBayar} disabled={saving} style={{ background: '#7c3aed', borderColor: '#7c3aed' }}>
                {saving ? 'Menyimpan...' : <><Wallet size={16} /> Simpan & Buat Jurnal</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ MODAL FAKTUR (BUAT/EDIT) ═══════════════ */}
      {fakturModal && (
        <div className="modal-overlay" onClick={() => setFakturModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h3><FileText size={18} style={{ marginRight: 8 }} />{fakturModal.editing ? 'Edit Faktur Pembelian' : 'Buat Faktur Pembelian'}</h3>
              <button className="btn-icon" onClick={() => setFakturModal(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><span>{error}</span></div>}

              {!fakturModal.editing && (
                <div className="form-group">
                  <label>Dari Penerimaan</label>
                  <select value={fakturModal.form.penerimaan_id} onChange={e => setFakturModal({ ...fakturModal, form: { ...fakturModal.form, penerimaan_id: e.target.value } })}>
                    <option value="">-- Pilih Penerimaan --</option>
                    {penerimaan.map(p => (
                      <option key={p.id} value={p.id} disabled={p.jml_faktur > 0}>
                        {p.no_penerimaan} — {p.supplier_name || '-'} ({rupiah(p.total_amount)}){p.jml_faktur > 0 ? ' [sudah ada faktur]' : ''}
                      </option>
                    ))}
                  </select>
                  {penerimaan.filter(p => p.jml_faktur > 0).length > 0 && (
                    <div style={{ fontSize: '0.7rem', color: '#b45309', marginTop: 4 }}>Penerimaan yang sudah punya faktur dinonaktifkan.</div>
                  )}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group"><label>No. Faktur</label>
                  <input type="text" value={fakturModal.form.no_faktur} onChange={e => setFakturModal({ ...fakturModal, form: { ...fakturModal.form, no_faktur: e.target.value } })} placeholder="Otomatis jika kosong" />
                </div>
                <div className="form-group"><label>Tanggal</label>
                  <input type="date" value={fakturModal.form.tanggal} onChange={e => setFakturModal({ ...fakturModal, form: { ...fakturModal.form, tanggal: e.target.value } })} />
                </div>
                <div className="form-group"><label>Status</label>
                  <select value={fakturModal.form.status} onChange={e => setFakturModal({ ...fakturModal, form: { ...fakturModal.form, status: e.target.value } })}>
                    <option value="Belum Dibayar">Belum Dibayar</option>
                    <option value="Lunas">Lunas</option>
                  </select>
                </div>
                <div className="form-group"><label>Catatan</label>
                  <input type="text" value={fakturModal.form.notes} onChange={e => setFakturModal({ ...fakturModal, form: { ...fakturModal.form, notes: e.target.value } })} placeholder="Catatan (opsional)" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setFakturModal(null)}>Batal</button>
              <button className="btn btn-primary" onClick={handleFakturSave} disabled={saving}>
                {saving ? 'Menyimpan...' : <><FileText size={16} /> Simpan Faktur</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ MODAL DETAIL (LIhat) ═══════════════ */}
      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 980 }}>
            <div className="modal-header">
              <h3><Eye size={18} style={{ marginRight: 8 }} />{detail.title}</h3>
              <button className="btn-icon" onClick={() => setDetail(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#f0f4ff', borderRadius: 10, padding: '12px 16px', marginBottom: 14, border: '1px solid #d6e4ff', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><span style={{ fontSize: '0.7rem', color: '#64748b' }}>Supplier</span><div style={{ fontWeight: 600 }}>{detail.header.supplier_name || '-'}</div></div>
                <div><span style={{ fontSize: '0.7rem', color: '#64748b' }}>Gudang</span><div style={{ fontWeight: 600 }}>{detail.header.gudang_nama || '-'}</div></div>
                {detail.header.no_po && (
                  <div><span style={{ fontSize: '0.7rem', color: '#64748b' }}>No. PO</span><div style={{ fontWeight: 600 }}>{detail.header.no_po || '-'}</div></div>
                )}
                {detail.header.no_penerimaan && (
                  <div><span style={{ fontSize: '0.7rem', color: '#64748b' }}>No. Penerimaan</span><div style={{ fontWeight: 600 }}>{detail.header.no_penerimaan || '-'}</div></div>
                )}
                <div><span style={{ fontSize: '0.7rem', color: '#64748b' }}>Tanggal</span><div style={{ fontWeight: 600 }}>{detail.header.tanggal || '-'}</div></div>
                {Number(detail.header.ppn) > 0 && (
                  <div><span style={{ fontSize: '0.7rem', color: '#64748b' }}>PPN ({Number(detail.header.ppn_persen) || 11}%)</span><div style={{ fontWeight: 600, color: '#7c3aed' }}>{rupiah(detail.header.ppn)}</div></div>
                )}
                {detail.header.metode_pembayaran && (
                  <div><span style={{ fontSize: '0.7rem', color: '#64748b' }}>Metode Bayar</span><div style={{ fontWeight: 600 }}>{detail.header.metode_pembayaran || '-'}</div></div>
                )}
                {detail.header.status && (
                  <div><span style={{ fontSize: '0.7rem', color: '#64748b' }}>Status</span><div><span className={`badge ${badgeStatus(detail.header.status)}`}>{detail.header.status}</span></div></div>
                )}
                {(detail.header.dibuat_oleh || detail.header.diperiksa_oleh) && (
                  <>
                    <div><span style={{ fontSize: '0.7rem', color: '#64748b' }}>Dibuat Oleh</span><div style={{ fontWeight: 600 }}>{detail.header.dibuat_oleh || '-'}</div></div>
                    <div><span style={{ fontSize: '0.7rem', color: '#64748b' }}>Diperiksa Oleh</span><div style={{ fontWeight: 600 }}>{detail.header.diperiksa_oleh || '-'}</div></div>
                  </>
                )}
              </div>

              <div className="table-wrapper">
                <table className="data-table table-fit" style={{ '--tab-color': detailTabColor, '--tab-text': '#ffffff' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '4%' }}>No</th><th style={{ width: '12%' }}>Kode</th><th style={{ width: detail.verify ? '22%' : '28%' }}>Nama Sparepart</th><th style={{ width: '7%' }}>Satuan</th>
                      {detail.kind === 'penerimaan' && <th style={{ width: '9%' }}>Dipesan</th>}
                      <th style={{ width: '8%' }}>{detail.kind === 'penerimaan' ? 'Diterima' : 'Qty'}</th><th style={{ width: '14%' }}>Harga Satuan</th><th style={{ width: '15%' }}>Subtotal</th>
                      {detail.kind === 'po' && detail.verify && (
                        <>
                          <th style={{ width: '12%' }}>Status</th><th style={{ width: '11%' }}>Qty Disetujui</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {detail.items.map((it, i) => (
                      <tr key={i}>
                        <td className="nowrap-cell">{i + 1}</td>
                        <td className="nowrap-cell">{it.code || '-'}</td>
                        <td>{it.name || '-'}</td>
                        <td className="nowrap-cell">{it.unit || '-'}</td>
                        {detail.kind === 'penerimaan' && <td className="nowrap-cell">{it.qty_dipesan || 0}</td>}
                        <td className="nowrap-cell">{detail.kind === 'penerimaan' ? it.qty_diterima : it.qty}</td>
                        <td className="nowrap-cell">{rupiah(it.harga_satuan)}</td>
                        <td className="nowrap-cell" style={{ fontWeight: 600 }}>{rupiah(it.subtotal)}</td>
                        {detail.kind === 'po' && detail.verify && (
                          <>
                            <td className="nowrap-cell">
                              <select
                                value={it.status_verifikasi}
                                onChange={e => updVerifItem(i, 'status_verifikasi', e.target.value)}
                                style={{ padding: '5px 8px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.75rem', minWidth: 100 }}
                              >
                                <option value="Disetujui">Disetujui</option>
                                <option value="Pending">Pending</option>
                                <option value="Ditolak">Ditolak</option>
                              </select>
                            </td>
                            <td className="nowrap-cell">
                              <input
                                type="number"
                                value={it.qty_verifikasi}
                                disabled={it.status_verifikasi === 'Ditolak' || it.status_verifikasi === 'Pending'}
                                min={0} max={it.qty}
                                onChange={e => updVerifItem(i, 'qty_verifikasi', e.target.value)}
                                style={{ width: 76, padding: '5px 8px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.75rem', textAlign: 'right' }}
                              />
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={detail.kind === 'penerimaan' ? 6 : (detail.verify ? 7 : 5)} style={{ textAlign: 'right', fontWeight: 700 }}>Total</td>
                      <td colSpan={2} style={{ fontWeight: 800, color: '#075985' }}>{rupiah(detail.header.total_amount)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {detail.kind === 'po' && detail.verify && !detail.verifyPending && (
                <div style={{ marginTop: 10, fontSize: '0.78rem', color: '#64748b', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px' }}>
                  <strong>Tips verifikasi:</strong> pilih <b>Ditolak</b> untuk item yang tidak disetujui (tidak akan masuk ke penerimaan barang), atau sesuaikan <b>Qty Disetujui</b> bila hanya sebagian qty yang disetujui. Gunakan tombol <b>Pending</b> bila PO ditunda (masuk tab PO Pending) atau <b>Tolak</b> untuk menolak seluruh PO.
                </div>
              )}
              {detail.kind === 'po' && detail.verifyPending && (
                <div style={{ marginTop: 10, fontSize: '0.78rem', color: '#64748b', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px' }}>
                  <strong>Proses item pending:</strong> hanya item berstatus <b>Pending</b> yang ditampilkan. Pilih keputusan (Disetujui / Pending / Ditolak) lalu klik <b>Simpan Keputusan Item</b>. Item yang disetujui akan masuk ke penerimaan barang, yang tetap pending akan kembali tampil di tab ini.
                </div>
              )}

              {detail.kind === 'penerimaan' && (Number(detail.header.diskon_persen) > 0 || Number(detail.header.diskon_rupiah) > 0 || Number(detail.header.biaya_lain) > 0 || Number(detail.header.ppn) > 0) && (
                <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end', gap: 24, fontSize: '0.8rem' }}>
                  {(Number(detail.header.diskon_persen) > 0 || Number(detail.header.diskon_rupiah) > 0) && (
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ color: '#64748b', fontSize: '0.7rem' }}>Diskon Faktur{Number(detail.header.diskon_persen) > 0 ? ` (${detail.header.diskon_persen}%)` : ''}</span>
                      <div style={{ fontWeight: 600, color: '#dc2626' }}>-{rupiah(Number(detail.header.diskon_rupiah) || 0)}</div>
                    </div>
                  )}
                  {Number(detail.header.biaya_lain) > 0 && (
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ color: '#64748b', fontSize: '0.7rem' }}>Biaya Lain{detail.header.biaya_lain_catatan ? ` (${detail.header.biaya_lain_catatan})` : ''}</span>
                      <div style={{ fontWeight: 600, color: '#b45309' }}>+{rupiah(detail.header.biaya_lain)}</div>
                    </div>
                  )}
                  {Number(detail.header.ppn) > 0 && (
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ color: '#64748b', fontSize: '0.7rem' }}>PPN ({Number(detail.header.ppn_persen) || 11}%)</span>
                      <div style={{ fontWeight: 600, color: '#7c3aed' }}>+{rupiah(detail.header.ppn)}</div>
                    </div>
                  )}
                </div>
              )}

              {detail.header.notes && (
                <div style={{ marginTop: 10, fontSize: '0.8rem', color: '#64748b' }}>
                  <strong>Catatan:</strong> {detail.header.notes}
                </div>
              )}
            </div>
            <div className="modal-footer">
              {detail.kind === 'po' && detail.verify && !detail.verifyPending && (
                <>
                  <button className="btn btn-primary" onClick={() => handleVerifikasiItem('Disetujui')} disabled={saving}>
                    <ShieldCheck size={16} style={{ marginRight: 6 }} />{saving ? 'Menyimpan...' : 'Setujui'}
                  </button>
                  <button className="btn btn-secondary" onClick={() => handleVerifikasiItem('Pending')} disabled={saving} style={{ color: '#b45309', borderColor: '#f59e0b' }}>
                    <Clock size={16} style={{ marginRight: 6 }} />Pending
                  </button>
                  <button className="btn btn-danger" onClick={() => handleVerifikasiItem('Ditolak')} disabled={saving}>
                    <XCircle size={16} style={{ marginRight: 6 }} />Tolak PO
                  </button>
                </>
              )}
              {detail.kind === 'po' && detail.verifyPending && (
                <button className="btn btn-primary" onClick={() => handleVerifikasiItem('Disetujui')} disabled={saving}>
                  <ShieldCheck size={16} style={{ marginRight: 6 }} />{saving ? 'Menyimpan...' : 'Simpan Keputusan Item'}
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => setDetail(null)}>Tutup</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}